import React, { useState, useEffect, useRef } from 'react';
import { uses12h } from '../utils/timeFormat';
import { a11y } from '../utils/a11yLabels';
import DialogCard from './DialogCard';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAnchoredPanel } from '../hooks/useAnchoredPanel';
import { syncNativeAlarms } from '../utils/nativeAlarms';
import { useSubCalendars } from '../context/SubCalendarContext';
import apiClient from '../api/client';
import { canShowPurchase } from '../utils/platform';
import { useT } from '../i18n';
import { ALARM_KEYS, getNotifSettings } from '../utils/sounds';
import { invalidateEventCache } from '../utils/viewCache';
import { registerUnsavedGuard } from '../utils/unsavedGuard';
import { track } from '../utils/analytics';
import { useWhisper } from '../hooks/useWhisper';
import { MicIcon, StopIcon, CameraIcon, TrashIcon, ClipIcon, BellIcon, BackIcon } from './icons';
import MultiEventReview from './MultiEventReview';
import IconPicker from './IconPicker';
import { iconOf, stripIcon, withIcon } from './EventIcon';
import { CheckSquare } from './RoundBtn';

// Alert type labels per language
const ALERT_TYPE_L = {
  it: { message: '✉️ Messaggio', alarm: '🔔 Allarme'   },
  en: { message: '✉️ Message',   alarm: '🔔 Alarm'     },
  es: { message: '✉️ Mensaje',   alarm: '🔔 Alarma'    },
  de: { message: '✉️ Nachricht', alarm: '🔔 Alarm'     },
  fr: { message: '✉️ Message',   alarm: '🔔 Alarme'    },
  nl: { message: '✉️ Bericht',   alarm: '🔔 Alarm'     },
  pt: { message: '✉️ Mensagem',  alarm: '🔔 Alarme'    },
  ru: { message: '✉️ Сообщение', alarm: '🔔 Будильник' },
  pl: { message: '✉️ Wiadomość', alarm: '🔔 Alarm'     },
  ro: { message: '✉️ Mesaj',     alarm: '🔔 Alarmă'    },
  zh: { message: '✉️ 消息',       alarm: '🔔 闹钟'       },
  ja: { message: '✉️ メッセージ',  alarm: '🔔 アラーム'    },
  hi: { message: '✉️ संदेश',      alarm: '🔔 अलार्म'     },
  ko: { message: '✉️ 메시지',      alarm: '🔔 알람'       },
  ar: { message: '✉️ رسالة',      alarm: '🔔 منبّه'      },
};

// NOTE: the "All day" wording comes from i18n.js (T.form.allDay). It used to be
// duplicated here and in EventCard as well — three copies of two words.

// nth occurrence (1..5) of a weekday within its month, or -1 when it is the
// LAST such weekday of the month — powers "every 4th Thursday" / "every last Friday".
function nthWeekdayOfMonth(date) {
  const day = date.getDate();
  const nth = Math.ceil(day / 7);
  const plus7 = new Date(date);
  plus7.setDate(day + 7);
  return plus7.getMonth() !== date.getMonth() ? -1 : nth;
}

// Build a Google Calendar RRULE string from the recurrence state object.
// startDate (the event's start) is needed for MONTHLY "nth weekday" mode.
function buildRRule(rec, startDate) {
  if (!rec.freq) return null;
  let rule = `RRULE:FREQ=${rec.freq}`;
  if (rec.interval && rec.interval > 1) rule += `;INTERVAL=${rec.interval}`;
  if (rec.freq === 'WEEKLY' && rec.byDay.length > 0) {
    rule += `;BYDAY=${rec.byDay.join(',')}`;
  }
  if (rec.freq === 'MONTHLY' && rec.monthlyMode === 'weekday' && startDate) {
    const d = new Date(startDate);
    if (!isNaN(d)) {
      const WD  = ['SU','MO','TU','WE','TH','FR','SA'][d.getDay()];
      rule += `;BYDAY=${nthWeekdayOfMonth(d)}${WD}`;
    }
  }
  if (rec.endType === 'count' && rec.count > 0) {
    rule += `;COUNT=${rec.count}`;
  } else if (rec.endType === 'until' && rec.until) {
    const d = new Date(rec.until + 'T23:59:59Z');
    const pad = n => String(n).padStart(2, '0');
    rule += `;UNTIL=${d.getUTCFullYear()}${pad(d.getUTCMonth()+1)}${pad(d.getUTCDate())}T235959Z`;
  }
  return rule;
}

const EMPTY_RECURRENCE = { freq: null, byDay: [], endType: 'never', count: 10, until: '', interval: 1, monthlyMode: 'day' };

// Thin separator line between form sections. Declared HERE, at module level, and
// not inside EventForm: a component defined inside the render body is a brand
// new type on every render, so React threw away all six dividers and rebuilt
// them for every single letter typed in the title. Typing felt sticky on slow
// phones because of it.
const Divider = () => <div style={{ height: 1, background: 'var(--color-border)' }} />;

// Rotating wheel for a fixed list of values; snaps to the value in the centre
// row (the highlighted one). Touch-scrollable on mobile via CSS scroll-snap.
function Wheel({ values, value, onChange, suffix, disabled, big, flat }) {
  const ref = React.useRef(null);
  const settleRef = React.useRef(null);
  const ITEM_H = big ? 46 : 38;
  const VISIBLE = big ? 5 : 3;  // odd → the middle row is the selected one
  const PAD = Math.floor(VISIBLE / 2);
  const idx = Math.max(0, values.indexOf(value));

  React.useEffect(() => {
    const el = ref.current;
    if (el && el.scrollTop !== idx * ITEM_H) el.scrollTop = idx * ITEM_H;
  }, [idx]);

  function handleScroll() {
    clearTimeout(settleRef.current);
    settleRef.current = setTimeout(() => {
      const el = ref.current; if (!el) return;
      let i = Math.round(el.scrollTop / ITEM_H);
      i = Math.max(0, Math.min(values.length - 1, i));
      if (el.scrollTop !== i * ITEM_H) el.scrollTop = i * ITEM_H;
      if (values[i] !== value) onChange(values[i]);
    }, 90);
  }

  return (
    <div style={{ position: 'relative', height: ITEM_H * VISIBLE, width: big ? 80 : 62, overflow: 'hidden', opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      {/* No box around the middle row in the flat style. Silvia sent a
          photograph of Google's counter and what makes it read at a glance is
          that the chosen number is simply DARKER and bigger than the two either
          side of it — a bordered box around it is a second thing to look at,
          saying what the weight already says. */}
      {!flat && (
        <div style={{ position: 'absolute', top: ITEM_H * PAD, left: 2, right: 2, height: ITEM_H, background: 'var(--color-surface)', border: '1.5px solid var(--color-primary)', borderRadius: 10, pointerEvents: 'none' }} />
      )}
      <div ref={ref} className="wheel-scroll" onScroll={handleScroll}
        style={{
          height: '100%', overflowY: 'scroll', scrollSnapType: 'y mandatory', scrollbarWidth: 'none',
          // A tester in the USA, trying to move her summary from 7:00 to 6:00:
          // "when I try and change the seven it's moving the background and not
          // the numbers."
          //
          // This is scroll chaining. A short scrollable strip that reaches its
          // end hands the rest of the gesture to whatever is behind it — so the
          // page slid while the hour sat still, and it looked like the wheel was
          // simply broken. Worse on a small wheel, because three rows reach the
          // end almost immediately.
          //
          // `contain` keeps the gesture in here: at the end of the list nothing
          // happens, which is the honest answer, instead of something else
          // moving. `pan-y` tells the browser up front that a vertical drag
          // belongs to this element, so it does not spend the first few
          // milliseconds deciding and give them to the page.
          overscrollBehavior: 'contain',
          touchAction: 'pan-y',
          WebkitOverflowScrolling: 'touch',
        }}>
        <div style={{ height: ITEM_H * PAD }} />
        {values.map((v, i) => (
          <div key={v} onClick={() => { if (ref.current) ref.current.scrollTop = i * ITEM_H; onChange(v); }}
            style={{
              height: ITEM_H, display: 'flex', alignItems: 'center', justifyContent: 'center', scrollSnapAlign: 'center', cursor: 'pointer',
              fontSize: v === value ? (big ? '1.9rem' : '1.4rem') : (big ? '1.25rem' : '1.05rem'), fontWeight: v === value ? 800 : 500,
              color: v === value ? (flat ? 'var(--color-text)' : 'var(--color-primary-dark)') : 'var(--color-text-muted)',
              opacity: v === value ? 1 : (flat ? 0.42 : 0.5),
            }}>
            {v}
          </div>
        ))}
        <div style={{ height: ITEM_H * PAD }} />
      </div>
    </div>
  );
}

// The three wheels always show the same values, so they are built ONCE, here,
// and not on every mount of the picker. They used to be useMemo'd inside
// TimeSelect — which is unmounted and rebuilt every time the modal is opened,
// so the memo never survived to be of any use.
// Every five minutes, which is what the photograph shows and what a calendar
// is actually used for — sixty rows to reach :55 was a long scroll for a thing
// nobody needs at that precision.
//
// But an event can already BE at 13:07 — one dictated by voice, one read off a
// photograph, one made in Google. So the minute in hand is added to the list
// when it is not a multiple of five: the wheel can always show what the event
// says, and you are never forced to move an appointment just to look at it.
const MINUTE_STEP_5 = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));
function minutesFor(m) {
  if (!m || MINUTE_STEP_5.includes(m)) return MINUTE_STEP_5;
  return [...MINUTE_STEP_5, m].sort();
}
const HOUR24_VALUES = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const HOUR12_VALUES = Array.from({ length: 12 }, (_, i) => String(i + 1)); // '1'..'12'
const PERIOD_VALUES = ['AM', 'PM'];

// Time picker: rotating wheels. value/onChange always work on a 24-hour "HH:mm"
// string (so the rest of the app is unchanged), but when `hour12` is true it
// shows a 12-hour face (hours 1–12 + an AM/PM wheel) for English locales.
function TimeSelect({ value, onChange, disabled, hour12, big, flat }) {
  const [h, m] = (value || '09:00').split(':');
  const sep = { color: 'var(--color-text-muted)', fontWeight: 800, fontSize: '1.2rem' };

  if (hour12) {
    const h24 = parseInt(h, 10) || 0;
    const period = h24 < 12 ? 'AM' : 'PM';
    let n = h24 % 12; if (n === 0) n = 12;
    const h12 = String(n);
    const to24 = (nh12, per) => { let hh = parseInt(nh12, 10) % 12; if (per === 'PM') hh += 12; return String(hh).padStart(2, '0'); };
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
        <Wheel values={HOUR12_VALUES} value={h12} onChange={nh => onChange(to24(nh, period) + ':' + m)} disabled={disabled} big={big} flat={flat} />
        <span style={sep}>:</span>
        <Wheel values={minutesFor(m)} value={m} onChange={mm => onChange(h + ':' + mm)} disabled={disabled} big={big} flat={flat} />
        <Wheel values={PERIOD_VALUES} value={period} onChange={p => onChange(to24(h12, p) + ':' + m)} disabled={disabled} big={big} flat={flat} />
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
      <Wheel values={HOUR24_VALUES} value={h} onChange={hh => onChange(hh + ':' + m)} suffix="h" disabled={disabled} big={big} flat={flat} />
      <span style={sep}>:</span>
      <Wheel values={minutesFor(m)} value={m} onChange={mm => onChange(h + ':' + mm)} suffix="m" disabled={disabled} big={big} flat={flat} />
    </span>
  );
}

// Compact time button that opens a full-size modal with the rotating wheels
// (tap the time → big picker → ✓ to confirm). value/onChange stay "HH:mm" 24h.
export function TimeField({ value, onChange, disabled, hour12, label }) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);
  const btnRef = React.useRef(null);
  const panelRef = React.useRef(null);
  // Kept on the screen by the shared hook now, not by arithmetic of its own.
  // The counter went off the right-hand edge in Settings, was measured and
  // nudged back, and STILL went off — because an absolutely positioned panel
  // is measured inside its ancestors and moved inside them too, while the
  // window it was being compared against is somewhere else entirely.
  // See hooks/useAnchoredPanel.js: fixed position, viewport coordinates,
  // one implementation for every panel that had this bug.
  const panelPos = useAnchoredPanel(open, btnRef, panelRef, { align: 'start' });
  // How far the counter has to slide back to stay on the screen.
  //
  // It opens under the time it belongs to, which is right — but in Settings the
  // morning-summary time sits near the right-hand edge, so half the counter was
  // off the screen. Silvia: "l'orologio per metà è fuori dallo schermo".
  //
  // Measured rather than guessed: the panel is drawn, its real edges are read,
  // and it is nudged left by however much it overhangs. A rule like "open it to
  // the left when the field is past the middle" would be a guess about widths
  // that change with the language.
  // An empty value means nobody has chosen yet, and the field says so. It used
  // to open showing the current minute — 21:07 — which is not a time anybody
  // picks for anything, and MeMo then warned about an overlap with it. A
  // default that looks like a decision is worse than no default.
  const unset = !value;
  const [h, m] = (value || '09:00').split(':');
  let display;
  if (unset) {
    display = '--:--';
  } else if (hour12) {
    const h24 = parseInt(h, 10) || 0; const p = h24 < 12 ? 'AM' : 'PM'; let n = h24 % 12; if (n === 0) n = 12;
    display = `${n}:${m} ${p}`;
  } else {
    display = `${h}:${m}`;
  }
  // A menu that will not close is a trap: outside tap and Escape both shut it.
  React.useEffect(() => {
    if (!open) return undefined;
    const away = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const esc = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('touchstart', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('touchstart', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button ref={btnRef} type="button" disabled={disabled} onClick={() => setOpen(o => !o)} aria-expanded={open} aria-label={unset ? (label || display) : display}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 'var(--radius-sm)',
          // Open, the chip is filled the way Silvia's photograph shows the one
          // being edited: quietly marked, so you can see WHICH time the counter
          // below belongs to.
          border: '1.5px solid var(--color-primary)',
          background: open ? 'var(--color-primary-light)' : 'var(--color-card)',
          color: 'var(--color-text)', fontSize: '0.88rem', fontWeight: 700, fontFamily: 'inherit', cursor: disabled ? 'default' : 'pointer', flexShrink: 0 }}>
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" style={{ flexShrink: 0, opacity: 0.75 }}
          stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" /><path d="M12 7v5.2l3.2 2" />
        </svg>
        {display}
      </button>
      {/* The counter opens UNDER the time, in the form — not as a black
          screen with a card floating on it. Silvia sent Google's and it is
          better for a reason worth keeping: you still see the date, the other
          time and the title while you turn the hour, so you are choosing a
          time inside a day rather than answering a question that has covered
          the day up. */}
      {open && (
        <div
          ref={panelRef}
          style={{
            zIndex: 60,
            width: 'max-content',
            ...(panelPos || { position: 'fixed', top: -9999, left: -9999 }),
            background: 'var(--color-card)',
            border: '1.5px solid var(--color-border)',
            borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            padding: '6px 10px',
            display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          <TimeSelect value={value || '09:00'} onChange={onChange} hour12={hour12} flat />
          <button type="button" onClick={() => setOpen(false)} aria-label="OK"
            style={{ background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: '50%', width: 34, height: 34, flexShrink: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" stroke="currentColor" fill="none" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4.5 4.5L19 7"/></svg>
          </button>
        </div>
      )}
    </div>
  );
}

// Convert a Date object → "YYYY-MM-DDTHH:mm" (local, no timezone shift)
function dateToLocalISO(date) {
  const p = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}T${p(date.getHours())}:${p(date.getMinutes())}`;
}

// Let the browser draw what we have just asked for before we take the thread
// away from it again. Setting state does not paint; the paint happens when the
// current task ends, so heavy work started in the same task lands BEFORE the
// spinner anybody is waiting for.
function paint() {
  return new Promise(resolve => {
    if (typeof requestAnimationFrame !== 'function') { setTimeout(resolve, 0); return; }
    requestAnimationFrame(() => setTimeout(resolve, 0));
  });
}

// Resize + recompress an image file using Canvas before sending to the server.
// Targets max 1600px on the long edge at 85% JPEG quality — good for OCR, ~200-500 KB output.
const MAX_SIDE = 1600;

/**
 * Shrink a phone photo before sending it.
 *
 * A camera photo is around 4032×3024 — twelve million pixels, roughly 48 MB
 * once decoded. The old way decoded ALL of it into an <img>, then drew the
 * whole thing down to 1600 on a canvas: two full-size passes on a phone,
 * seconds of them, and the reason Silvia sees the scan as slow.
 *
 * createImageBitmap can decode and shrink in one step, off the main thread,
 * and it never holds the full-size image at all. Where it exists this is
 * several times faster and the interface keeps responding while it happens.
 * Where it does not, the old path is still here, unchanged.
 */
function compressImage(file) {
  const scaled = (w, h) => {
    if (w <= MAX_SIDE && h <= MAX_SIDE) return { width: w, height: h };
    return w >= h
      ? { width: MAX_SIDE, height: Math.round(h * MAX_SIDE / w) }
      : { width: Math.round(w * MAX_SIDE / h), height: MAX_SIDE };
  };

  const fast = async () => {
    // Ask for the small version up front: the browser is allowed to skip
    // pixels it will never need, which is where the time goes.
    const probe = await createImageBitmap(file);
    const { width, height } = scaled(probe.width, probe.height);
    probe.close?.();
    const bmp = await createImageBitmap(file, { resizeWidth: width, resizeHeight: height, resizeQuality: 'medium' });
    const canvas = document.createElement('canvas');
    canvas.width = width; canvas.height = height;
    canvas.getContext('2d').drawImage(bmp, 0, 0);
    bmp.close?.();
    const blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', 0.85));
    if (!blob) throw new Error('toBlob');       // fall back rather than send nothing
    return blob;
  };

  const slow = () => new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const { width, height } = scaled(img.width, img.height);
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      canvas.toBlob(blob => resolve(blob || file), 'image/jpeg', 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

  if (typeof createImageBitmap !== 'function') return slow();
  return fast().catch(slow);
}

// Physically rotate an image blob by a multiple of 90° using Canvas.
// FIX: Pass a pre-compressed blob to avoid OOM on large phone photos (4032×3024).
// FIX: Falls back to toDataURL when toBlob returns null (iOS Safari / mobile memory limits).
function rotateBlob(blob, degrees) {
  return new Promise((resolve) => {
    if (degrees === 0) { resolve(blob); return; }
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const rad  = (degrees * Math.PI) / 180;
      const swap = degrees === 90 || degrees === 270;
      const canvas = document.createElement('canvas');
      canvas.width  = swap ? img.height : img.width;
      canvas.height = swap ? img.width  : img.height;
      const ctx = canvas.getContext('2d');
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(rad);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      canvas.toBlob((b) => {
        if (b) { resolve(b); return; }
        // toBlob returned null — fall back to toDataURL (always works on iOS Safari)
        try {
          const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
          const binStr  = atob(dataUrl.split(',')[1]);
          const bytes   = new Uint8Array(binStr.length);
          for (let i = 0; i < binStr.length; i++) bytes[i] = binStr.charCodeAt(i);
          resolve(new Blob([bytes], { type: 'image/jpeg' }));
        } catch (_) {
          resolve(blob); // last resort: return the unrotated blob
        }
      }, 'image/jpeg', 0.92);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(blob); };
    img.src = url;
  });
}

// Extract a rectangular crop region from a blob using canvas.
// cropRect values are normalized 0–1 coordinates relative to image dimensions.
function cropBlob(blob, { x1, y1, x2, y2 }) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const cropX = Math.round(x1 * img.width);
      const cropY = Math.round(y1 * img.height);
      const cropW = Math.round((x2 - x1) * img.width);
      const cropH = Math.round((y2 - y1) * img.height);
      const canvas = document.createElement('canvas');
      canvas.width  = cropW;
      canvas.height = cropH;
      canvas.getContext('2d').drawImage(img, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
      canvas.toBlob((b) => {
        if (b) { resolve(b); return; }
        try {
          const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
          const binStr  = atob(dataUrl.split(',')[1]);
          const bytes   = new Uint8Array(binStr.length);
          for (let i = 0; i < binStr.length; i++) bytes[i] = binStr.charCodeAt(i);
          resolve(new Blob([bytes], { type: 'image/jpeg' }));
        } catch (_) {
          resolve(blob);
        }
      }, 'image/jpeg', 0.92);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(blob); };
    img.src = url;
  });
}

const ALLOWED_ATT_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'application/pdf'];
// Photos above this go through the same resize the scanner uses. A phone photo
// is 3–8 MB; as base64 in memory it is a third bigger again, and five of them
// were enough to have Android kill the tab and take the whole event with it.
const ATT_COMPRESS_OVER = 1_200_000;

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function todayLocalISO() {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
}

function isoToLocalInput(isoString) {
  if (!isoString) return todayLocalISO();
  const d = new Date(isoString);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function subtractOneDay(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d - 1));
  const p = n => String(n).padStart(2, '0');
  return `${dt.getUTCFullYear()}-${p(dt.getUTCMonth() + 1)}-${p(dt.getUTCDate())}`;
}

// Photo modal labels — keyed by base language (2-char)
// Fields: title (rotate mode), cropTitle (crop mode), scan, cancel, crop (enter-crop btn), back (exit-crop btn)
const PHOTO_LABELS = {
  it: { title: 'Ruota la foto',      cropTitle: 'Ritaglia la foto',     scan: '🔍 Scansiona',   cancel: 'Annulla',   crop: '✂️ Ritaglia',   back: '← Indietro' },
  es: { title: 'Rotar foto',         cropTitle: 'Recortar foto',        scan: '🔍 Escanear',    cancel: 'Cancelar',  crop: '✂️ Recortar',   back: '← Volver'   },
  de: { title: 'Foto drehen',        cropTitle: 'Foto zuschneiden',     scan: '🔍 Scannen',     cancel: 'Abbrechen', crop: '✂️ Schneiden',  back: '← Zurück'   },
  fr: { title: 'Faire pivoter',      cropTitle: 'Rogner la photo',      scan: '🔍 Scanner',     cancel: 'Annuler',   crop: '✂️ Rogner',     back: '← Retour'   },
  nl: { title: 'Foto draaien',       cropTitle: 'Foto bijsnijden',      scan: '🔍 Scannen',     cancel: 'Annuleren', crop: '✂️ Bijsnijden', back: '← Terug'    },
  pt: { title: 'Girar foto',         cropTitle: 'Recortar foto',        scan: '🔍 Digitalizar', cancel: 'Cancelar',  crop: '✂️ Recortar',   back: '← Voltar'   },
  ru: { title: 'Повернуть',          cropTitle: 'Обрезать фото',        scan: '🔍 Сканировать', cancel: 'Отмена',    crop: '✂️ Обрезать',   back: '← Назад'    },
  pl: { title: 'Obróć zdjęcie',      cropTitle: 'Przytnij zdjęcie',     scan: '🔍 Skanuj',      cancel: 'Anuluj',    crop: '✂️ Przytnij',   back: '← Wróć'     },
  ro: { title: 'Rotiți fotografia',  cropTitle: 'Decupați fotografia',  scan: '🔍 Scanați',     cancel: 'Anulați',   crop: '✂️ Decupați',   back: '← Înapoi'   },
  en: { title: 'Rotate photo',       cropTitle: 'Crop photo',           scan: '🔍 Scan',        cancel: 'Cancel',    crop: '✂️ Crop',       back: '← Back'     },
  zh: { title: '旋转照片',            cropTitle: '裁剪照片',              scan: '🔍 扫描',         cancel: '取消',       crop: '✂️ 裁剪',        back: '← 返回'      },
  ja: { title: '写真を回転',           cropTitle: '写真を切り抜き',         scan: '🔍 スキャン',      cancel: 'キャンセル',   crop: '✂️ 切り抜き',     back: '← 戻る'      },
  hi: { title: 'फ़ोटो घुमाएँ',          cropTitle: 'फ़ोटो काटें',            scan: '🔍 स्कैन',        cancel: 'रद्द करें',     crop: '✂️ काटें',       back: '← वापस'     },
  ko: { title: '사진 회전',            cropTitle: '사진 자르기',            scan: '🔍 스캔',         cancel: '취소',        crop: '✂️ 자르기',      back: '← 뒤로'      },
  ar: { title: 'تدوير الصورة',        cropTitle: 'قصّ الصورة',            scan: '🔍 مسح',         cancel: 'إلغاء',       crop: '✂️ قصّ',        back: '→ رجوع'     },
};

const CROP_HINTS = {
  it: "Trascina gli angoli per delimitare l'area da leggere",
  es: 'Arrastra las esquinas para delimitar el área a leer',
  de: 'Ecken ziehen, um den Lesebereich einzugrenzen',
  fr: 'Faites glisser les coins pour délimiter la zone',
  nl: 'Sleep de hoeken om het leesgebied af te bakenen',
  pt: 'Arraste os cantos para delimitar a área de leitura',
  ru: 'Перетащите углы для выделения области чтения',
  pl: 'Przeciągnij rogi, aby ograniczyć obszar do odczytu',
  ro: 'Trageți colțurile pentru a delimita zona de citit',
  en: 'Drag the corners to frame the area to read',
  zh: '拖动四角以框选要识别的区域',
  ja: '角をドラッグして読み取る範囲を指定',
  hi: 'पढ़ने वाले क्षेत्र को घेरने के लिए कोनों को खींचें',
  ko: '모서리를 끌어 읽을 영역을 지정하세요',
  ar: 'اسحب الزوايا لتحديد المنطقة المراد قراءتها',
};

const PREPARING_LABELS = {
  it: 'Preparazione…', es: 'Preparando…',    de: 'Vorbereitung…',
  fr: 'Préparation…',  nl: 'Voorbereiden…',  pt: 'Preparando…',
  ru: 'Подготовка…',   pl: 'Przygotowanie…', ro: 'Pregătire…',  en: 'Preparing…',
  zh: '准备中…',        ja: '準備中…',         hi: 'तैयारी हो रही है…',
  ko: '준비 중…',        ar: 'جارٍ التحضير…',
};

// Labels for the camera popover menu (camera vs. gallery picker)
const CAM_MENU_LABELS = {
  it: { camera: '📷 Fotocamera', gallery: '🖼️ Galleria'  },
  es: { camera: '📷 Cámara',     gallery: '🖼️ Galería'   },
  de: { camera: '📷 Kamera',     gallery: '🖼️ Galerie'   },
  fr: { camera: '📷 Appareil',   gallery: '🖼️ Galerie'   },
  nl: { camera: '📷 Camera',     gallery: '🖼️ Galerij'  },
  pt: { camera: '📷 Câmera',     gallery: '🖼️ Galeria'  },
  ru: { camera: '📷 Камера',     gallery: '🖼️ Галерея'  },
  pl: { camera: '📷 Aparat',     gallery: '🖼️ Galeria'  },
  ro: { camera: '📷 Cameră',     gallery: '🖼️ Galerie'  },
  en: { camera: '📷 Camera',     gallery: '🖼️ Gallery'  },
  zh: { camera: '📷 相机',        gallery: '🖼️ 相册'      },
  ja: { camera: '📷 カメラ',      gallery: '🖼️ ギャラリー' },
  hi: { camera: '📷 कैमरा',       gallery: '🖼️ गैलरी'     },
  ko: { camera: '📷 카메라',      gallery: '🖼️ 갤러리'     },
  ar: { camera: '📷 الكاميرا',    gallery: '🖼️ المعرض'    },
};

// Shown (blocking, before leaving the form) when the event saved but its MeMo
// reminders did not — with the native calendar reminders off for reachable
// users, a lost alert means an event that never rings, and the user must know.
const ALERTS_NOT_SAVED = {
  it: "Evento salvato, ma i promemoria NON sono stati aggiornati (problema di connessione?). Apri l'evento e salvalo di nuovo per riattivarli.",
  en: 'Event saved, but its reminders were NOT updated (connection problem?). Open the event and save it again to re-arm them.',
  es: 'Evento guardado, pero los recordatorios NO se actualizaron (¿problema de conexión?). Abre el evento y guárdalo de nuevo para reactivarlos.',
  de: 'Termin gespeichert, aber die Erinnerungen wurden NICHT aktualisiert (Verbindungsproblem?). Öffne den Termin und speichere ihn erneut, um sie zu reaktivieren.',
  fr: "Événement enregistré, mais les rappels n'ont PAS été mis à jour (problème de connexion ?). Ouvrez l'événement et enregistrez-le à nouveau pour les réactiver.",
  nl: 'Afspraak opgeslagen, maar de herinneringen zijn NIET bijgewerkt (verbindingsprobleem?). Open de afspraak en sla hem opnieuw op om ze te heractiveren.',
  pt: 'Evento guardado, mas os lembretes NÃO foram atualizados (problema de ligação?). Abre o evento e guarda-o novamente para os reativar.',
  ru: 'Событие сохранено, но напоминания НЕ обновились (проблема с соединением?). Откройте событие и сохраните его ещё раз, чтобы включить их снова.',
  pl: 'Wydarzenie zapisane, ale przypomnienia NIE zostały zaktualizowane (problem z połączeniem?). Otwórz wydarzenie i zapisz je ponownie, aby je przywrócić.',
  ro: 'Eveniment salvat, dar mementourile NU au fost actualizate (problemă de conexiune?). Deschide evenimentul și salvează-l din nou pentru a le reactiva.',
  zh: '事件已保存，但提醒未能更新（网络问题？）。请打开该事件并重新保存以重新启用提醒。',
  ja: '予定は保存されましたが、リマインダーは更新されませんでした（接続の問題？）。予定を開いてもう一度保存すると再設定されます。',
  hi: 'इवेंट सहेजा गया, लेकिन रिमाइंडर अपडेट नहीं हुए (कनेक्शन समस्या?)। इवेंट खोलकर दोबारा सहेजें ताकि वे फिर से चालू हो जाएँ।',
  ko: '일정은 저장되었지만 알림은 업데이트되지 않았습니다(연결 문제?). 일정을 열어 다시 저장하면 알림이 다시 설정됩니다.',
  ar: 'تم حفظ الحدث، لكن التذكيرات لم تُحدَّث (مشكلة في الاتصال؟). افتح الحدث واحفظه مرة أخرى لإعادة تفعيلها.',
};

// Shown in the reminder section while the event's saved reminders are still
// being read back from the server (edit mode only), and when that read failed.
// Until they are in hand the form must not touch them: saving used to fire a
// DELETE with an empty list and wipe reminders the user had never even seen.
const REMINDERS_LOADING = {
  it: 'Carico i promemoria…',            en: 'Loading reminders…',
  es: 'Cargando recordatorios…',         de: 'Erinnerungen werden geladen…',
  fr: 'Chargement des rappels…',         nl: 'Herinneringen laden…',
  pt: 'A carregar lembretes…',           ru: 'Загрузка напоминаний…',
  pl: 'Wczytywanie przypomnień…',        ro: 'Se încarcă mementourile…',
  zh: '正在加载提醒…',                     ja: 'リマインダーを読み込み中…',
  hi: 'रिमाइंडर लोड हो रहे हैं…',            ko: '알림을 불러오는 중…',
  ar: 'جارٍ تحميل التذكيرات…',
};

const REMINDERS_LOAD_FAILED = {
  it: 'Non sono riuscito a leggere i promemoria di questo evento. Puoi salvare lo stesso: i promemoria restano come sono.',
  en: "Couldn't read this event's reminders. You can still save: the reminders stay as they are.",
  es: 'No pude leer los recordatorios de este evento. Puedes guardar igualmente: los recordatorios se quedan como están.',
  de: 'Die Erinnerungen dieses Termins konnten nicht gelesen werden. Du kannst trotzdem speichern: die Erinnerungen bleiben unverändert.',
  fr: "Impossible de lire les rappels de cet événement. Vous pouvez enregistrer quand même : les rappels restent inchangés.",
  nl: 'Kon de herinneringen van deze afspraak niet lezen. Je kunt toch opslaan: de herinneringen blijven zoals ze zijn.',
  pt: 'Não consegui ler os lembretes deste evento. Podes guardar na mesma: os lembretes ficam como estão.',
  ru: 'Не удалось прочитать напоминания этого события. Сохранить всё равно можно: напоминания останутся прежними.',
  pl: 'Nie udało się odczytać przypomnień tego wydarzenia. Możesz zapisać mimo to: przypomnienia pozostaną bez zmian.',
  ro: 'Nu am putut citi mementourile acestui eveniment. Poți salva oricum: mementourile rămân neschimbate.',
  zh: '无法读取此日程的提醒。仍可保存：提醒将保持不变。',
  ja: 'この予定のリマインダーを読み込めませんでした。そのまま保存できます（リマインダーは現状のままです）。',
  hi: 'इस इवेंट के रिमाइंडर नहीं पढ़ पाए। आप फिर भी सहेज सकते हैं: रिमाइंडर जैसे हैं वैसे ही रहेंगे।',
  ko: '이 일정의 알림을 불러오지 못했습니다. 그대로 저장할 수 있으며, 알림은 변경되지 않습니다.',
  ar: 'تعذّر قراءة تذكيرات هذا الحدث. يمكنك الحفظ على أي حال: ستبقى التذكيرات كما هي.',
};

// Shown when the event saved but one or more attachments did not reach the
// server. Same reasoning as ALERTS_NOT_SAVED: a file that silently vanished is
// worse than a warning, because the user only finds out weeks later.
const ATTACHMENTS_NOT_SAVED = {
  it: (n) => `Evento salvato, ma ${n} allegato/i NON è stato caricato (connessione o file troppo grande). Riapri l'evento e riprova ad allegarlo.`,
  en: (n) => `Event saved, but ${n} attachment(s) were NOT uploaded (connection, or file too big). Open the event again and retry.`,
  es: (n) => `Evento guardado, pero ${n} adjunto(s) NO se subieron (conexión o archivo demasiado grande). Abre el evento y vuelve a intentarlo.`,
  de: (n) => `Termin gespeichert, aber ${n} Anhang/Anhänge wurden NICHT hochgeladen (Verbindung oder Datei zu groß). Öffne den Termin und versuche es erneut.`,
  fr: (n) => `Événement enregistré, mais ${n} pièce(s) jointe(s) n'ont PAS été envoyées (connexion ou fichier trop volumineux). Rouvrez l'événement et réessayez.`,
  nl: (n) => `Afspraak opgeslagen, maar ${n} bijlage(n) zijn NIET geüpload (verbinding of bestand te groot). Open de afspraak en probeer opnieuw.`,
  pt: (n) => `Evento guardado, mas ${n} anexo(s) NÃO foram carregados (ligação ou ficheiro demasiado grande). Abre o evento e tenta de novo.`,
  ru: (n) => `Событие сохранено, но ${n} вложение(й) НЕ загружено (соединение или слишком большой файл). Откройте событие и попробуйте снова.`,
  pl: (n) => `Wydarzenie zapisane, ale ${n} załącznik(ów) NIE przesłano (połączenie lub zbyt duży plik). Otwórz wydarzenie i spróbuj ponownie.`,
  ro: (n) => `Eveniment salvat, dar ${n} atașament(e) NU au fost încărcate (conexiune sau fișier prea mare). Deschide evenimentul și încearcă din nou.`,
  zh: (n) => `事件已保存，但有 ${n} 个附件未能上传（网络问题或文件过大）。请重新打开该事件后再试。`,
  ja: (n) => `予定は保存されましたが、${n} 件の添付ファイルがアップロードされませんでした（接続またはサイズ超過）。予定を開き直して再試行してください。`,
  hi: (n) => `इवेंट सहेजा गया, लेकिन ${n} अटैचमेंट अपलोड नहीं हुए (कनेक्शन या फ़ाइल बहुत बड़ी)। इवेंट दोबारा खोलकर फिर कोशिश करें।`,
  ko: (n) => `일정은 저장되었지만 첨부파일 ${n}개가 업로드되지 않았습니다(연결 문제 또는 파일 용량 초과). 일정을 다시 열어 재시도하세요.`,
  ar: (n) => `تم حفظ الحدث، لكن ${n} من المرفقات لم تُرفع (اتصال أو ملف كبير جدًا). أعد فتح الحدث وحاول مجددًا.`,
};

// What actually went wrong when a save fails. The raw axios/server string was
// being shown to the user: "Network Error", "Request failed with status code
// 500". True, and useless — it never says whether the event was saved, nor what
// to do next. The two cases that matter are told apart:
//   offline  → the request never left the phone. Nothing was saved. Just retry.
//   noAnswer → the request left but no answer came back. It MAY have been saved,
//              so "retry" can quietly create a twin. Check the list first.
const SAVE_ERRORS = {
  it: { offline: "Sembri offline: l'evento NON è stato salvato. Riprova quando torna la rete.", noAnswer: "Il server non ha risposto. Prima di riprovare guarda nella lista: l'evento potrebbe essere già stato creato.", noDate: 'Manca la data, o non è una data valida.' },
  en: { offline: 'You seem to be offline: the event was NOT saved. Try again when you have signal.', noAnswer: 'The server did not answer. Before retrying, check the list: the event may already be there.', noDate: 'The date is missing, or is not a valid date.' },
  es: { offline: 'Pareces estar sin conexión: el evento NO se guardó. Inténtalo cuando vuelva la red.', noAnswer: 'El servidor no respondió. Antes de reintentar, mira la lista: el evento puede estar ya creado.', noDate: 'Falta la fecha, o no es una fecha válida.' },
  de: { offline: 'Du scheinst offline zu sein: der Termin wurde NICHT gespeichert. Versuche es mit Verbindung erneut.', noAnswer: 'Der Server hat nicht geantwortet. Prüfe die Liste, bevor du es erneut versuchst: der Termin könnte schon da sein.', noDate: 'Das Datum fehlt oder ist ungültig.' },
  fr: { offline: "Vous semblez hors ligne : l'événement n'a PAS été enregistré. Réessayez avec du réseau.", noAnswer: "Le serveur n'a pas répondu. Avant de réessayer, vérifiez la liste : l'événement existe peut-être déjà.", noDate: "La date manque, ou n'est pas valide." },
  nl: { offline: 'Je lijkt offline: de afspraak is NIET opgeslagen. Probeer het opnieuw met verbinding.', noAnswer: 'De server antwoordde niet. Kijk eerst in de lijst: de afspraak staat er misschien al.', noDate: 'De datum ontbreekt of is ongeldig.' },
  pt: { offline: 'Parece que estás offline: o evento NÃO foi guardado. Tenta de novo com rede.', noAnswer: 'O servidor não respondeu. Antes de tentar de novo, vê a lista: o evento pode já existir.', noDate: 'Falta a data, ou não é uma data válida.' },
  ru: { offline: 'Похоже, вы офлайн: событие НЕ сохранено. Повторите при появлении сети.', noAnswer: 'Сервер не ответил. Перед повтором проверьте список: событие могло уже создаться.', noDate: 'Дата отсутствует или недействительна.' },
  pl: { offline: 'Wygląda na brak połączenia: wydarzenie NIE zostało zapisane. Spróbuj ponownie z siecią.', noAnswer: 'Serwer nie odpowiedział. Zanim spróbujesz ponownie, sprawdź listę: wydarzenie może już istnieć.', noDate: 'Brakuje daty lub data jest nieprawidłowa.' },
  ro: { offline: 'Pari offline: evenimentul NU a fost salvat. Încearcă din nou când ai semnal.', noAnswer: 'Serverul nu a răspuns. Înainte să reîncerci, verifică lista: evenimentul poate exista deja.', noDate: 'Lipsește data, sau data nu este validă.' },
  zh: { offline: '你似乎处于离线状态：日程未保存。恢复网络后再试。', noAnswer: '服务器没有响应。重试前请先查看列表，该日程可能已创建。', noDate: '缺少日期，或日期无效。' },
  ja: { offline: 'オフラインのようです。予定は保存されていません。通信が戻ってから再試行してください。', noAnswer: 'サーバーから応答がありませんでした。再試行の前に一覧を確認してください。すでに作成されている可能性があります。', noDate: '日付がないか、正しくありません。' },
  hi: { offline: 'आप ऑफ़लाइन लगते हैं: इवेंट सहेजा नहीं गया। नेटवर्क आने पर फिर कोशिश करें।', noAnswer: 'सर्वर ने जवाब नहीं दिया। दोबारा कोशिश करने से पहले सूची देखें: इवेंट पहले से बन चुका हो सकता है।', noDate: 'तारीख़ नहीं है, या मान्य नहीं है।' },
  ko: { offline: '오프라인 상태로 보입니다: 일정이 저장되지 않았습니다. 연결 후 다시 시도하세요.', noAnswer: '서버가 응답하지 않았습니다. 다시 시도하기 전에 목록을 확인하세요. 이미 만들어졌을 수 있습니다.', noDate: '날짜가 없거나 올바르지 않습니다.' },
  ar: { offline: 'يبدو أنك غير متصل: لم يُحفظ الحدث. أعد المحاولة عند عودة الشبكة.', noAnswer: 'لم يستجب الخادم. قبل إعادة المحاولة راجع القائمة: قد يكون الحدث أُنشئ بالفعل.', noDate: 'التاريخ مفقود أو غير صالح.' },
};

// Told while saving, so a long save stops looking like a frozen button.
const SAVE_STAGES = {
  it: { event: "Salvo l'evento…", alerts: 'Salvo i promemoria…', files: 'Carico gli allegati' },
  en: { event: 'Saving the event…', alerts: 'Saving reminders…', files: 'Uploading attachments' },
  es: { event: 'Guardando el evento…', alerts: 'Guardando recordatorios…', files: 'Subiendo adjuntos' },
  de: { event: 'Termin wird gespeichert…', alerts: 'Erinnerungen werden gespeichert…', files: 'Anhänge werden hochgeladen' },
  fr: { event: "Enregistrement de l'événement…", alerts: 'Enregistrement des rappels…', files: 'Envoi des pièces jointes' },
  nl: { event: 'Afspraak opslaan…', alerts: 'Herinneringen opslaan…', files: 'Bijlagen uploaden' },
  pt: { event: 'A guardar o evento…', alerts: 'A guardar lembretes…', files: 'A carregar anexos' },
  ru: { event: 'Сохраняю событие…', alerts: 'Сохраняю напоминания…', files: 'Загружаю вложения' },
  pl: { event: 'Zapisywanie wydarzenia…', alerts: 'Zapisywanie przypomnień…', files: 'Wysyłanie załączników' },
  ro: { event: 'Se salvează evenimentul…', alerts: 'Se salvează mementourile…', files: 'Se încarcă atașamentele' },
  zh: { event: '正在保存日程…', alerts: '正在保存提醒…', files: '正在上传附件' },
  ja: { event: '予定を保存中…', alerts: 'リマインダーを保存中…', files: '添付ファイルをアップロード中' },
  hi: { event: 'इवेंट सहेजा जा रहा है…', alerts: 'रिमाइंडर सहेजे जा रहे हैं…', files: 'अटैचमेंट अपलोड हो रहे हैं' },
  ko: { event: '일정 저장 중…', alerts: '알림 저장 중…', files: '첨부파일 업로드 중' },
  ar: { event: 'جارٍ حفظ الحدث…', alerts: 'جارٍ حفظ التذكيرات…', files: 'جارٍ رفع المرفقات' },
};

const RETRY_LABELS = {
  it: { retry: 'Riprova', leave: 'Esci comunque', another: '📷 Un\'altra foto', crop: '✂️ Ritaglia e riprova', again: '🎤 Ridetta' },
  en: { retry: 'Retry', leave: 'Leave anyway', another: '📷 Another photo', crop: '✂️ Crop and retry', again: '🎤 Say it again' },
  es: { retry: 'Reintentar', leave: 'Salir igualmente', another: '📷 Otra foto', crop: '✂️ Recortar y reintentar', again: '🎤 Dictar otra vez' },
  de: { retry: 'Erneut versuchen', leave: 'Trotzdem verlassen', another: '📷 Anderes Foto', crop: '✂️ Zuschneiden und erneut', again: '🎤 Nochmal sprechen' },
  fr: { retry: 'Réessayer', leave: 'Quitter quand même', another: '📷 Autre photo', crop: '✂️ Rogner et réessayer', again: '🎤 Redicter' },
  nl: { retry: 'Opnieuw', leave: 'Toch sluiten', another: '📷 Andere foto', crop: '✂️ Bijsnijden en opnieuw', again: '🎤 Opnieuw inspreken' },
  pt: { retry: 'Tentar de novo', leave: 'Sair mesmo assim', another: '📷 Outra foto', crop: '✂️ Recortar e repetir', again: '🎤 Ditar de novo' },
  ru: { retry: 'Повторить', leave: 'Всё равно выйти', another: '📷 Другое фото', crop: '✂️ Обрезать и повторить', again: '🎤 Продиктовать снова' },
  pl: { retry: 'Ponów', leave: 'Wyjdź mimo to', another: '📷 Inne zdjęcie', crop: '✂️ Przytnij i ponów', again: '🎤 Podyktuj ponownie' },
  ro: { retry: 'Reîncearcă', leave: 'Ieși oricum', another: '📷 Altă poză', crop: '✂️ Decupează și reîncearcă', again: '🎤 Dictează din nou' },
  zh: { retry: '重试', leave: '仍然离开', another: '📷 换一张照片', crop: '✂️ 裁剪后重试', again: '🎤 重新口述' },
  ja: { retry: '再試行', leave: 'このまま出る', another: '📷 別の写真', crop: '✂️ 切り抜いて再試行', again: '🎤 もう一度話す' },
  hi: { retry: 'फिर कोशिश', leave: 'फिर भी बाहर', another: '📷 दूसरी फ़ोटो', crop: '✂️ काटकर फिर कोशिश', again: '🎤 फिर बोलें' },
  ko: { retry: '다시 시도', leave: '그래도 나가기', another: '📷 다른 사진', crop: '✂️ 자르고 다시', again: '🎤 다시 말하기' },
  ar: { retry: 'إعادة المحاولة', leave: 'الخروج على أي حال', another: '📷 صورة أخرى', crop: '✂️ قصّ وأعد', again: '🎤 أعد الإملاء' },
};

// Asked before walking away from an event that has never been saved.
// Offered for a few seconds after the back arrow empties the form.
const UNDO_CLEAR = {
  it: 'Annulla',        en: 'Undo',           es: 'Deshacer',
  de: 'Rückgängig',     fr: 'Annuler',        nl: 'Ongedaan maken',
  pt: 'Anular',         ru: 'Отменить',       pl: 'Cofnij',
  ro: 'Anulează',       zh: '撤销',            ja: '元に戻す',
  hi: 'पहले जैसा',       ko: '실행 취소',        ar: 'تراجع',
};

const DISCARD_EVENT = {
  it: { text: 'Vuoi uscire senza salvare questo impegno?', stay: 'No, resta', leave: 'Sì, esci' },
  en: { text: 'Leave without saving this event?', stay: 'No, stay', leave: 'Yes, leave' },
  es: { text: '¿Salir sin guardar esta cita?', stay: 'No, quedarme', leave: 'Sí, salir' },
  de: { text: 'Ohne Speichern verlassen?', stay: 'Nein, bleiben', leave: 'Ja, verlassen' },
  fr: { text: "Quitter sans enregistrer cet événement ?", stay: 'Non, rester', leave: 'Oui, quitter' },
  nl: { text: 'Weggaan zonder deze afspraak op te slaan?', stay: 'Nee, blijven', leave: 'Ja, weggaan' },
  pt: { text: 'Sair sem guardar este evento?', stay: 'Não, ficar', leave: 'Sim, sair' },
  ru: { text: 'Выйти, не сохранив событие?', stay: 'Нет, остаться', leave: 'Да, выйти' },
  pl: { text: 'Wyjść bez zapisania wydarzenia?', stay: 'Nie, zostań', leave: 'Tak, wyjdź' },
  ro: { text: 'Ieși fără să salvezi evenimentul?', stay: 'Nu, rămân', leave: 'Da, ies' },
  zh: { text: '不保存就离开吗？', stay: '不，留下', leave: '是，离开' },
  ja: { text: '保存せずに移動しますか？', stay: 'いいえ', leave: 'はい' },
  hi: { text: 'इस इवेंट को सहेजे बिना बाहर जाएँ?', stay: 'नहीं, रुकें', leave: 'हाँ, बाहर' },
  ko: { text: '저장하지 않고 나갈까요?', stay: '아니요', leave: '예, 나가기' },
  ar: { text: 'الخروج دون حفظ هذا الحدث؟', stay: 'لا، ابقَ', leave: 'نعم، اخرج' },
};

// Shown when /edit/:id points at something that is not there any more.
const EVENT_GONE = {
  it: { text: 'Questo impegno non esiste più.', home: 'Torna agli impegni di oggi' },
  en: { text: 'This event no longer exists.', home: "Back to today's events" },
  es: { text: 'Esta cita ya no existe.', home: 'Volver a las citas de hoy' },
  de: { text: 'Diesen Termin gibt es nicht mehr.', home: 'Zurück zu den heutigen Terminen' },
  fr: { text: "Cet événement n'existe plus.", home: "Retour aux événements du jour" },
  nl: { text: 'Deze afspraak bestaat niet meer.', home: 'Terug naar vandaag' },
  pt: { text: 'Este evento já não existe.', home: 'Voltar aos eventos de hoje' },
  ru: { text: 'Этого события больше нет.', home: 'К сегодняшним событиям' },
  pl: { text: 'To wydarzenie już nie istnieje.', home: 'Wróć do dzisiejszych wydarzeń' },
  ro: { text: 'Acest eveniment nu mai există.', home: 'Înapoi la evenimentele de azi' },
  zh: { text: '该日程已不存在。', home: '返回今天的日程' },
  ja: { text: 'この予定はもうありません。', home: '今日の予定に戻る' },
  hi: { text: 'यह इवेंट अब मौजूद नहीं है।', home: 'आज के इवेंट पर लौटें' },
  ko: { text: '이 일정은 더 이상 없습니다.', home: '오늘 일정으로 돌아가기' },
  ar: { text: 'هذا الحدث لم يعد موجودًا.', home: 'العودة إلى أحداث اليوم' },
};

// Opening /edit/:id used to depend entirely on the event being handed over by
// the navigation state. A refresh, a link you sent yourself, or Android killing
// the PWA in the background and restoring it later all arrived with empty
// hands, and the form quietly bounced you to the home screen — the work you
// were looking at simply gone, with no explanation.
// This shell resolves the event first, asking the server when it has to, and
// only then builds the form.
export default function EventForm() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const { state } = useLocation();
  const passed = state?.event;
  const isEdit = Boolean(editId);
  const baseL = (user?.language || 'it').split('-')[0];
  const G = EVENT_GONE[baseL] || EVENT_GONE.en;

  const [fetched, setFetched] = useState(null);
  const [status, setStatus]   = useState(isEdit && !passed ? 'loading' : 'ready');

  useEffect(() => {
    if (!isEdit || passed) return;
    let alive = true;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    apiClient.get(`/events/${encodeURIComponent(editId)}`, { params: { tz } })
      // Only a real event will do. Anything else — an empty body, an unexpected
      // shape — must land on the "it is not there" screen rather than build a
      // form around nothing.
      .then(r => {
        if (!alive) return;
        if (r.data && r.data.id) { setFetched(r.data); setStatus('ready'); }
        else setStatus('gone');
      })
      .catch(() => { if (alive) setStatus('gone'); });
    return () => { alive = false; };
  }, [isEdit, passed, editId]);

  if (isEdit && !passed) {
    if (status === 'loading') return <div className="spinner" style={{ margin: '40px auto' }} />;
    if (status !== 'ready' || !fetched) {
      return (
        <div style={{ padding: '40px 8px', textAlign: 'center' }}>
          <p style={{ fontSize: '0.95rem', color: 'var(--color-text-muted)', marginBottom: 18 }}>{G.text}</p>
          <button type="button" className="btn btn-outline" onClick={() => navigate('/', { replace: true })}>
            {G.home}
          </button>
        </div>
      );
    }
    return <EventFormBody editEvent={fetched} />;
  }
  return <EventFormBody editEvent={passed} />;
}

function EventFormBody({ editEvent }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const { search: _locationSearch } = useLocation();
  const isEdit = Boolean(editId);

  const lang     = user?.language || 'it';
  const baseLang = lang.split('-')[0];
  const A        = a11y(lang);   // names for the symbol-only buttons

  // The floating buttons step back while you are busy with the form.
  //
  // Four 64px circles run down the right edge and sit on top of the fields. On
  // a small screen they cover the reminder row, and the only way to read what
  // was underneath them was to scroll the page up and down.
  //
  // So they fade — they do not move. A control that jumps around is worse than
  // one you can see through, and a moving target is worse for everybody but
  // especially for anyone with unsteady hands. Full strength comes back after a
  // second of stillness, and instantly if a finger lands on them, so reaching
  // for one is never aiming at a ghost. Declared here, above the JSX that reads
  // it: state read from higher up the file than it is defined is a temporal
  // dead zone, and React answers that by unmounting the entire screen.
  const [fabDimmed, setFabDimmed] = useState(false);
  useEffect(() => {
    let timer = null;
    const busy = () => {
      setFabDimmed(true);
      clearTimeout(timer);
      timer = setTimeout(() => setFabDimmed(false), 1100);
    };
    // Scrolling and typing are the two moments you are looking THROUGH them.
    window.addEventListener('scroll', busy, { passive: true, capture: true });
    window.addEventListener('keydown', busy);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', busy, { capture: true });
      window.removeEventListener('keydown', busy);
    };
  }, []);
  const T = useT(lang);
  const { subCalendars } = useSubCalendars();
  const PL = PHOTO_LABELS[baseLang] || PHOTO_LABELS.en;
  // Building an Intl formatter is one of the more expensive things a browser
  // does. This one only depends on the language, so it is built once per
  // language and not once per render of the monthly-recurrence row.
  const weekdayFmt = React.useMemo(
    () => new Intl.DateTimeFormat(baseLang, { weekday: 'long' }), [baseLang]);

  // Everything below decides ONLY what the form looks like on the very first
  // render: where the user was in the calendar, what the tapped slot was, how
  // long the event being edited lasts. It used to be recomputed — URL parsing,
  // sessionStorage, four date constructions — on every single render, that is
  // on every letter typed, to produce a value nobody read again after mount.
  // Now it runs once, inside useState's lazy initialiser.
  const [form, setForm] = useState(() => {
    // URL params from SlotGridView tap: /new?date=YYYY-MM-DD&time=HH:MM
    const searchParams = !isEdit ? new URLSearchParams(_locationSearch) : null;
    const slotDate = searchParams?.get('date') || null;
    const slotTime = searchParams?.get('time') || null;
    // If the user was browsing a specific day in the calendar, default to that date
    const calDate = !isEdit ? (sessionStorage.getItem('memo_selected_date') || null) : null;
    const todayStr = new Date().toISOString().slice(0, 10);
    // Tapping a slot in the grid IS choosing a time, so that one keeps it.
    // Everything else opens on a day with the hour still blank: the person
    // says when, MeMo does not guess.
    // Nine o'clock, back at Silvia's request.
    //
    // The empty wheel was right about one thing and wrong about the cost. It
    // was right that the CURRENT minute is a terrible default — 21:07 is not a
    // time anybody picks for anything, and MeMo used to warn about an overlap
    // with it. But "--:--" made every single event one extra tap, and it opened
    // a whole path of its own: save with no time, be asked for one, be
    // reminded about All day. Nine o'clock costs nothing and cannot be mistaken
    // for a decision the app made about your day: it is plainly a starting
    // point, and it is one tap to move.
    const defaultDT = (slotDate && slotTime)
      ? `${slotDate}T${slotTime}`
      : (calDate && calDate !== todayStr)
        ? calDate + 'T09:00'
        : todayStr + 'T09:00';

    let editEndDatetime = '';
    if (isEdit && editEvent?.end) {
      if (editEvent.allDay) {
        const startDateStr = (editEvent.start || '').slice(0, 10);
        const endDateStr = (editEvent.end || '').slice(0, 10);
        const diffDays = (new Date(endDateStr + 'T00:00') - new Date(startDateStr + 'T00:00')) / 86400000;
        editEndDatetime = diffDays > 1 ? subtractOneDay(endDateStr) + 'T09:00' : '';
      } else {
        const startMs = new Date(editEvent.start || '').getTime();
        const endMs = new Date(editEvent.end || '').getTime();
        editEndDatetime = (endMs - startMs) > 61 * 60 * 1000 ? isoToLocalInput(editEvent.end) : '';
      }
    }

    return {
      title:         editEvent?.title             || '',
      type:          editEvent?.type              || '',
      // All-day events store start as a plain 'YYYY-MM-DD' — slice it directly to
      // avoid the timezone shift that Date parsing would introduce.
      datetime:      editEvent
        ? (editEvent.allDay
            ? (editEvent.start || '').slice(0, 10) + 'T09:00'
            : isoToLocalInput(editEvent.start))
        : defaultDT,
      allDay:        editEvent?.allDay            || false,
      endDatetime:   editEndDatetime,
      location:      editEvent?.location          || '',
      priority:      editEvent?.priority          || 'green',
      notes:         editEvent?.notes             || '',
      subCalendarId: editEvent?.subCalendar?.id   || '',
      recurrence: { ...EMPTY_RECURRENCE },
    };
  });
  // Initial date/time as loaded — used by the "edit entire series" flow: if the
  // user did NOT touch these, we omit them from the payload so the recurring
  // series keeps its original anchor instead of being re-anchored to the
  // occurrence that happened to be open (M5). Captured on first render only.
  const initialTimeRef = useRef({ datetime: form.datetime, endDatetime: form.endDatetime, allDay: form.allDay });

  // Reminder: multi-select — sound is a global device preference set in Settings.
  // NEW events get a default notification (type "message") at the lead time
  // configured in Settings → Notifiche (ON by default). The user can remove it
  // (disable the notification) and/or switch the type to "alarm".
  const [alertSet, setAlertSet] = useState(() => {
    if (editEvent?.alertMinutes != null) return new Set([editEvent.alertMinutes]);
    if (isEdit) return new Set(); // edit mode loads reminders from DB below
    const { enabled, defaultMin } = getNotifSettings();
    return enabled ? new Set([defaultMin]) : new Set();
  });
  // 'alarm' (rings) | 'message' (silent on-screen note). A NEW reminder rings:
  // see the note on NOTIF_KEYS.defaultType. In edit mode this is replaced a
  // moment later by whatever the event actually has stored.
  const [alertType, setAlertType] = useState(() => getNotifSettings().defaultType);
  // 'ready' | 'loading' | 'failed' — whether alertSet reflects what is actually
  // stored for this event. A NEW event has nothing to load, so it starts ready.
  const [alertsState, setAlertsState] = useState(isEdit ? 'loading' : 'ready');
  // Voice for main dictation (bottom action bar mic button)
  const { recording: mainRec, transcribing: mainTrans, startRecording: mainStart, stopRecording: mainStop, error: mainErr } = useWhisper(lang);

  // Voice transcription errors were previously invisible — surface the
  // "monthly AI limit reached" case, and any other failure (network / server /
  // mic) as the generic "couldn't understand, try again" message.
  useEffect(() => {
    if (mainErr === 'ai-limit') setParseError('limit');
    else if (mainErr) setParseError(true);
  }, [mainErr]);

  function handleMainMicToggle() {
    if (mainTrans) return;
    if (mainRec) { mainStop(); return; }
    mainStart(handleVoiceTranscript);
  }

  // Title grows onto new lines instead of overflowing to a second field.
  const titleRef = useRef(null);
  const dateRef  = useRef(null);

  // The OK button is pinned to the bottom-right corner, while the error banner
  // sits at the very top of the scrolling form. Pressing OK with an empty title
  // therefore looked like "nothing happens" — the explanation was off-screen.
  // Bring the offending field into view (and focus it, so the keyboard opens
  // right where the user has to type).
  // NOTE: the unsaved-work guard lives further down, after the state it reads.
  // It was originally written here and crashed the whole screen: `pendingFiles`
  // and `saving` are declared below, and reading them from up here is a
  // temporal dead zone — "Cannot access before initialization" — which React
  // answers by unmounting everything. A blank page, on the ＋ button.

  const SE = SAVE_ERRORS[baseLang] || SAVE_ERRORS.en;
  const RL = RETRY_LABELS[baseLang] || RETRY_LABELS.en;
  const SS = SAVE_STAGES[baseLang] || SAVE_STAGES.en;

  // Turn an axios failure into something a person can act on.
  function describeSaveError(err) {
    if (err?.response) {
      // The server answered and said no: its reason is the useful part.
      return err.response.data?.error || T.form.saveError;
    }
    // No answer at all. Either we never got out of the phone, or we did and the
    // reply was lost — and those two need opposite advice.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return SE.offline;
    if (err?.code === 'ERR_NETWORK' || err?.message === 'Network Error') return SE.offline;
    return SE.noAnswer;
  }

  function revealMissingField(which) {
    const el = which === 'date' ? dateRef.current : titleRef.current;
    if (!el) return;
    // After the state update, so the banner is already in the layout.
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (which !== 'date') el.focus();
    });
  }
  // How the event was filled in — for the 'event_created' analytics event.
  // Set to 'voice'/'photo' when those flows run; stays 'manual' otherwise.
  const sourceRef = useRef('manual');
  // Grow with the text, but never past ~4 lines: a very long title (a whole
  // dictated sentence, a pasted address) used to push the date, the priority
  // and everything else off the screen. Past the cap the field scrolls.
  const TITLE_MAX_H = 140;
  const autoSizeTitle = (el) => {
    if (!el) return;
    // NOTE: measuring means one forced layout per keystroke. Skipping it when
    // the text only got longer was tempting, but then the field would never
    // shrink back when you delete a line. Correctness wins: it is one small
    // element, and the expensive part (rebuilding every divider on each render)
    // is gone now — see the Divider note at the top of the file.
    el.style.height = 'auto';
    const needed = el.scrollHeight;
    el.style.height = Math.min(needed, TITLE_MAX_H) + 'px';
    el.style.overflowY = needed > TITLE_MAX_H ? 'auto' : 'hidden';
  };
  useEffect(() => { autoSizeTitle(titleRef.current); }, [form.title]);

  // Group visibility
  const [userGroups,      setUserGroups]      = useState([]);
  const [visibility,      setVisibility]      = useState(editEvent?.sharedGroupId ? 'group' : 'private');
  const [selectedGroupId, setSelectedGroupId] = useState(editEvent?.sharedGroupId || editEvent?.groupId || '');

  // Attachments (photos / PDF). pending = picked but not yet uploaded (they upload
  // on save, once the event has an id); existing = already-saved files (edit mode).
  const [pendingFiles, setPendingFiles] = useState([]);
  const [existingAtt,  setExistingAtt]  = useState([]);
  const [attError,     setAttError]     = useState(null);
  const [attBusy,      setAttBusy]      = useState(false); // reading/shrinking picked files
  const attInputRef = React.useRef(null);
  const [conflictMembers, setConflictMembers] = useState([]); // [{name, color, start}]
  // A date with no hour is not a moment. Everything that computes with the
  // start time has to know the difference, or a blank hour silently becomes
  // midnight — and an event at 00:00 clashes with nothing and sorts first.
  const hasTime = (dt) => /T\d{2}:\d{2}/.test(String(dt || ''));

  // The end as it is DISPLAYED: what has been chosen, or start + 1 hour, which
  // is what the server would do anyway with an event that arrives without one.
  // Built with a real Date so that 23:30 rolls over to the next day by itself.
  const endShown = (() => {
    if (form.endDatetime) return form.endDatetime;
    const day = (form.datetime || todayLocalISO()).slice(0, 10);
    const time = form.datetime?.split('T')[1]?.slice(0, 5) || '09:00';
    const [hh, mm] = time.split(':').map(Number);
    const dt = new Date(day + 'T00:00:00');
    dt.setHours((hh || 0) + 1, mm || 0, 0, 0);
    const p = n => String(n).padStart(2, '0');
    return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}T${p(dt.getHours())}:${p(dt.getMinutes())}`;
  })();
  const [personalClash, setPersonalClash] = useState(null); // { title, time } | null — gentle, non-blocking
  // Has anybody actually CHOSEN the time? The form opens at 9:00, which is a
  // sensible place to start and not a decision — so an overlap warning against
  // it is a warning about something nobody did. Silvia: "lo fai SOLO dopo che
  // l'utente ha inserito non solo il giorno ma anche l'ora". True from the
  // start when the time came from somewhere real: an event being edited, a
  // tapped slot in the planner, a dictated or scanned time.
  // slotDate/slotTime and searchParams all live inside the form's own
  // initialiser, so the query string is read again here rather than reaching
  // for variables that are not in scope. _locationSearch is the one that is.
  const [timeChosen, setTimeChosen] = useState(() => {
    if (isEdit) return true;
    const q = new URLSearchParams(_locationSearch || '');
    return Boolean(q.get('date') && q.get('time'));
  });
  // Unsaved-work guard. Only for a NEW event: an edit already has everything
  // safely on the server, and asking every time you back out of an event you
  // merely opened would be nagging, not protecting.
  const [askDiscard, setAskDiscard]       = useState(null); // null | () => void
  // What the back arrow last wiped, kept for a few seconds so it can be undone.
  const [clearedDraft, setClearedDraft]   = useState(null);
  const clearedTimerRef = useRef(null);
  const leavingOnPurpose                  = useRef(false);  // set by save/delete
  const [saving, setSaving]               = useState(false);
  // What the save is busy with right now: {kind:'event'|'alerts'|'files', done, total}
  const [saveStage, setSaveStage]         = useState(null);
  // The event saved but a reminder / an attachment did not — see doSave
  const [postSave, setPostSave]           = useState(null);
  const [retrying, setRetrying]           = useState(false);

  // ── Unsaved-work guard ──────────────────────────────────────────────────
  // Deliberately HERE and not higher up: it reads `form`, `pendingFiles` and
  // `saving`, and every one of those has to exist first.
  //
  // Is there anything to lose? A title, a place, notes or an attachment the
  // user put in by hand. A date alone does not count — the form arrives with
  // one already filled in, so counting it would make every screen "dirty" from
  // the start and the question meaningless.
  const isDirty = () => {
    if (isEdit || saving || leavingOnPurpose.current) return false;
    return Boolean(
      form.title.trim() || form.location.trim() || form.notes.trim() || pendingFiles.length > 0
    );
  };
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  /**
   * Leave without saving.
   *
   * Reuses the guard the bottom bar uses, so all the ways out ask the same
   * question: nothing typed → go straight away; something typed → the discard
   * dialog, and only "leave" actually leaves.
   *
   * Goes to Today rather than back one step. Back-one-step is nicer when you
   * arrived from a list, and unpredictable otherwise — from a notification, a
   * shared link, a cold start there may be nothing behind, and the phone would
   * close the app instead of closing the form. Somebody cancelling an event
   * they never meant to start should not lose the app as well.
   */
  /**
   * Back, in two steps — Silvia's design, and it is better than a plain ✕.
   *
   *   press once, with something written  →  the form is emptied
   *   press again, on an empty form       →  back to Today
   *
   * The reason is a small thing that happens constantly: you mistype the title
   * and want to start the sentence again. With a single exit you had to leave,
   * press ＋, and do the whole thing over — including the date you had already
   * set. Now the first press hands you back a blank form on the same day.
   *
   * The danger of this design is obvious and had to be answered: a back arrow
   * that ERASES is not what a back arrow usually does, and one stray tap after
   * dictating a long appointment would take all of it with no dialog. So the
   * cleared version is kept for a few seconds and offered back. Nothing is
   * asked before the clearing — that would defeat the point of the gesture —
   * and nothing is lost by it either.
   */
  function handleClose() {
    if (isDirtyRef.current()) {
      // Keep what is being wiped, and offer it back.
      setClearedDraft({ form, files: pendingFiles });
      clearTimeout(clearedTimerRef.current);
      clearedTimerRef.current = setTimeout(() => setClearedDraft(null), 7000);
      setForm(f => ({
        ...f,
        title: '', location: '', notes: '',
        subCalendarId: '', priority: 'green',
        recurrence: { ...EMPTY_RECURRENCE },
      }));
      setPendingFiles([]);
      return;
    }
    navigate('/');
  }

  /** Put back exactly what the last press wiped. */
  function undoClear() {
    if (!clearedDraft) return;
    clearTimeout(clearedTimerRef.current);
    setForm(clearedDraft.form);
    setPendingFiles(clearedDraft.files || []);
    setClearedDraft(null);
  }

  // Register with the bottom bar (see utils/unsavedGuard) and with the page
  // reload. Two of the three ways to leave; the phone's back button is below.
  useEffect(() => {
    const unregister = registerUnsavedGuard({
      isDirty: () => isDirtyRef.current(),
      ask: (proceed) => setAskDiscard(() => proceed),
    });
    const onBeforeUnload = (e) => {
      if (!isDirtyRef.current()) return;
      e.preventDefault();
      e.returnValue = ''; // required by Chrome to show its own dialog
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => { unregister(); window.removeEventListener('beforeunload', onBeforeUnload); };
  }, []);

  // Is there anything typed in right now? Kept as a plain value so the back-
  // button guard arms itself the moment the form stops being empty.
  const hasTypedSomething = Boolean(
    !isEdit && (form.title.trim() || form.location.trim() || form.notes.trim() || pendingFiles.length > 0)
  );

  // The phone's back button. Park an extra history entry while there is work to
  // lose, so the press lands here and asks instead of walking off the screen.
  useEffect(() => {
    if (!hasTypedSomething) return undefined;
    let armed = true;
    window.history.pushState({ memoEventForm: true }, '');
    const onPop = () => {
      if (!armed) return;
      if (!isDirtyRef.current()) { armed = false; navigate('/'); return; }
      window.history.pushState({ memoEventForm: true }, ''); // stay put, then ask
      setAskDiscard(() => () => { armed = false; navigate('/'); });
    };
    window.addEventListener('popstate', onPop);
    return () => { armed = false; window.removeEventListener('popstate', onPop); };
  }, [hasTypedSomething, navigate]);

  const [parsing, setParsing]             = useState(false);
  const [imageParsing, setImageParsing]   = useState(false);
  const [scanning, setScanning]           = useState(false);   // the photo button's own busy state
  const [errorKey, setErrorKey]           = useState(null);
  const [apiError, setApiError]           = useState(null);
  const [parseError, setParseError]       = useState(false);
  const [imageError, setImageError]       = useState(false);
  const [showImageMenu, setShowImageMenu] = useState(false);
  const [preparingPhoto, setPreparingPhoto] = useState(false);   // photo chosen, preview not up yet
  const [multiEvents, setMultiEvents]     = useState(null); // review list from a multi-event photo
  // Recurring-edit scope modal (shown when saving an edit to a recurring event)
  const [showRecurringEditModal, setShowRecurringEditModal] = useState(false);
  const [pendingPayload,         setPendingPayload]         = useState(null);
  // Delete (edit mode only)
  const [deleting,        setDeleting]        = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(null);  // null | 'single' | 'recurring'
  // Events always open directly editable. A read-only "view lock" used to keep
  // the entire <fieldset> (reminder, recurrence, label, everything) disabled
  // until you tapped a pencil to unlock. That confused users and, above all,
  // made the reminder controls unresponsive and impossible to clear. Tapping an
  // event now opens it ready to edit; the plumbing below stays but never fires.
  const readOnly = false;
  // photoPreview: { blob, dataUrl, rotation, mode: 'rotate'|'loading'|'crop', rotatedBlob } | null
  const [photoPreview, setPhotoPreview]   = useState(null);
  // Crop selection — normalized 0–1 coordinates
  const [cropRect, setCropRect]           = useState({ x1: 0.1, y1: 0.1, x2: 0.9, y2: 0.9 });
  const draggingRef = React.useRef(null); // 'tl'|'tr'|'bl'|'br'|null — ref avoids stale-closure bug
  // The last image actually sent to the scanner — so a failed scan can offer
  // "crop it and try again" instead of "take the photo all over again".
  const lastScanBlobRef     = React.useRef(null);
  const imageInputRef       = React.useRef(null);
  const cameraInputRef      = React.useRef(null);
  const previewContainerRef = React.useRef(null);

  // Fetch user's groups to show visibility selector
  React.useEffect(() => {
    apiClient.get('/groups')
      .then(r => {
        const groups = r.data || [];
        setUserGroups(groups);
        if (!selectedGroupId && groups.length > 0) setSelectedGroupId(groups[0].id);
      })
      // Deliberately quiet. This list only decides whether the "private /
      // shared" chooser is offered; it no longer decides what gets SAVED (see
      // the visibility line in handleSubmit), so a failed fetch can't turn a
      // shared event private any more. Nothing to warn about: an error message
      // about shared calendars would be noise for the many users who have none.
      .catch(() => {});
  }, []);

  // Conflict detection — debounced, fires when visibility=group and datetime/group changes
  React.useEffect(() => {
    if (visibility !== 'group' || !selectedGroupId || !form.datetime) {
      setConflictMembers([]);
      return;
    }
    const date = form.datetime.split('T')[0];
    const myStart = new Date(form.datetime);
    const myEnd = form.endDatetime
      ? new Date(form.endDatetime)
      : new Date(myStart.getTime() + 60 * 60 * 1000);

    const timer = setTimeout(() => {
      apiClient.get(`/groups/${selectedGroupId}/events?date=${date}`)
        .then(r => {
          const events = r.data || [];
          // Keep only other members' events that overlap with our selected time
          const conflicts = [];
          events.forEach(ev => {
            if (ev.isOwnEvent) return; // skip own events
            const oStart = new Date(ev.start);
            const oEnd   = ev.end ? new Date(ev.end) : new Date(oStart.getTime() + 60 * 60 * 1000);
            if (myStart < oEnd && myEnd > oStart) {
              // Format start time as HH:mm
              const pad = n => String(n).padStart(2, '0');
              const timeStr = `${pad(oStart.getHours())}:${pad(oStart.getMinutes())}`;
              conflicts.push({
                name:  ev.member?.name  || 'Unknown',
                color: ev.member?.color || '#888',
                time:  timeStr,
              });
            }
          });
          setConflictMembers(conflicts);
        })
        .catch(() => setConflictMembers([]));
    }, 600); // 600ms debounce

    return () => clearTimeout(timer);
  }, [visibility, selectedGroupId, form.datetime]);

  // Gentle, NON-blocking personal overlap notice. Saving is never gated (see
  // handleSubmit); this only surfaces a hint while you pick the time, debounced,
  // so a double-booking is visible but never interrupts. Skipped for group
  // events (handled above) and all-day events.
  React.useEffect(() => {
    // Not before there is an event to clash. The form opens with the current
    // time already filled in, so this used to run the moment it opened and
    // announce an overlap against a time nobody had chosen, on an event nobody
    // had named — Silvia: "non ho neanche scritto niente". A warning that
    // arrives before the work does is not a warning, it is noise, and noise is
    // what people learn to scroll past.
    if (!form.title.trim()) { setPersonalClash(null); return; }
    if (!timeChosen) { setPersonalClash(null); return; }
    if (visibility === 'group' || !hasTime(form.datetime) || form.allDay) { setPersonalClash(null); return; }
    const myStart = new Date(form.datetime).getTime();
    if (isNaN(myStart)) { setPersonalClash(null); return; }
    const myEnd = form.endDatetime ? new Date(form.endDatetime).getTime() : myStart + 3600000;
    const date = form.datetime.slice(0, 10);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timer = setTimeout(() => {
      apiClient.get('/events/today', { params: { tz, date } })
        .then(r => {
          const clash = (r.data || []).find(ev => {
            if (!ev.start || ev.allDay) return false;
            if (isEdit && ev.id === editId) return false;
            const es = new Date(ev.start).getTime();
            const ee = ev.end ? new Date(ev.end).getTime() : es + 3600000;
            return myStart < ee && es < myEnd;
          });
          if (!clash) { setPersonalClash(null); return; }
          const d = new Date(clash.start);
          const pad = n => String(n).padStart(2, '0');
          setPersonalClash({ title: clash.title || '', time: `${pad(d.getHours())}:${pad(d.getMinutes())}` });
        })
        .catch(() => setPersonalClash(null));
    }, 500);
    return () => clearTimeout(timer);
  }, [visibility, form.title, form.datetime, form.endDatetime, form.allDay, isEdit, editId, timeChosen]);

  // ── Attachments: load existing (edit mode), pick new files, remove ───────
  React.useEffect(() => {
    if (!isEdit || !editEvent?.id) return;
    let alive = true;
    apiClient.get('/attachments', { params: { googleEventId: editEvent.id } })
      .then(r => { if (alive) setExistingAtt(r.data || []); })
      // Not swallowed any more: an empty list and a failed read looked exactly
      // the same on screen, so a connection hiccup made the event's files
      // appear to have been deleted.
      .catch(() => { if (alive) setAttError(T.form.attLoadError || T.form.saveError); });
    return () => { alive = false; };
  }, [isEdit, editEvent?.id]);

  // Attachment allowances per plan (mirror of server utils/aiUsage.js):
  // Free 1×10MB · Personal 3×10MB · Premium 5×25MB · trial → Premium.
  const { ATT_MAX, ATT_MAX_BYTES } = React.useMemo(() => {
    const MB = 1024 * 1024;
    const created = user?.created_at ? new Date(user.created_at).getTime() : null;
    const inTrial = created == null || (Date.now() - created) < 100 * 86400000;
    const plan = (user?.plan || '').toLowerCase();
    const until = user?.plan_until ? new Date(user.plan_until).getTime() : null;
    const paidActive = plan && plan !== 'free' && (until == null || until >= Date.now());
    if (inTrial) return { ATT_MAX: 5, ATT_MAX_BYTES: 25 * MB };
    if (paidActive && plan === 'personal') return { ATT_MAX: 3, ATT_MAX_BYTES: 10 * MB };
    if (paidActive) return { ATT_MAX: 5, ATT_MAX_BYTES: 25 * MB };
    return { ATT_MAX: 1, ATT_MAX_BYTES: 10 * MB };
  }, [user?.created_at, user?.plan, user?.plan_until]);
  function inferMime(file) {
    if (file.type) return file.type;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    return { jpg:'image/jpeg', jpeg:'image/jpeg', png:'image/png', webp:'image/webp', heic:'image/heic', heif:'image/heif', pdf:'application/pdf' }[ext] || '';
  }
  // Every check now happens BEFORE the file is read. It used to be the other
  // way round: a file over the per-plan count was turned into base64 in full
  // and only then thrown away, and the "too many"/"too big" message was set
  // from inside a state updater — so picking five files at once left a single
  // message on screen and no way to tell WHICH ones had been dropped.
  async function handlePickFiles(e) {
    setAttError(null);
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-picking the same file later
    if (files.length === 0) return;

    let slots = ATT_MAX - existingAtt.length - pendingFiles.length;
    const problems = new Set();   // a Set: the same complaint is said once
    const accepted = [];

    for (const file of files) {
      if (slots <= 0) { problems.add(T.form.attTooMany); break; }
      const mime = inferMime(file);
      if (!ALLOWED_ATT_MIME.includes(mime)) { problems.add(T.form.attBadType); continue; }
      if (file.size > ATT_MAX_BYTES)        { problems.add(T.form.attTooBig);  continue; }
      accepted.push({ file, mime });
      slots--;
    }
    if (problems.size > 0) setAttError([...problems].join(' · '));
    if (accepted.length === 0) return;

    setAttBusy(true);
    try {
      for (const { file, mime } of accepted) {
        let blob = file, outMime = mime, name = file.name;
        // Shrink big photos, exactly like the scanner does. A holiday snap goes
        // from 6 MB to a few hundred KB with no loss of readability, which is
        // what the attachment is for.
        if (mime !== 'application/pdf' && file.size > ATT_COMPRESS_OVER) {
          const smaller = await compressImage(file);
          if (smaller && smaller.size > 0 && smaller.size < file.size) {
            blob = smaller;
            outMime = 'image/jpeg';
            name = name.replace(/\.[^.]+$/, '') + '.jpg';
          }
        }
        const dataBase64 = await blobToDataUrl(blob);
        setPendingFiles(prev => [...prev, { name, mime: outMime, size: blob.size, dataBase64 }]);
      }
    } catch (_) {
      setAttError(T.form.attBadType);   // unreadable file — the only way here
    } finally {
      setAttBusy(false);
    }
  }
  function removePending(idx) { setPendingFiles(prev => prev.filter((_, i) => i !== idx)); }
  function removeExisting(id) {
    // Optimistic, but no longer a lie: if the server refuses, the row comes
    // back. It used to vanish from the screen and stay on the server, so the
    // file was there again the next time the event was opened.
    const removed = existingAtt.find(a => a.id === id);
    setExistingAtt(prev => prev.filter(a => a.id !== id));
    apiClient.delete(`/attachments/${id}`).catch(() => {
      if (removed) setExistingAtt(prev => (prev.some(a => a.id === id) ? prev : [...prev, removed]));
      setAttError(T.form.attDeleteError || T.form.deleteError);
    });
  }

  // Load all alert times from DB when editing an existing event.
  // The outcome is tracked in alertsState, and doSave reads it: a failed load
  // used to look exactly like "this event has no reminders", so saving fired a
  // DELETE and wiped them. Now a failure means "leave the reminders alone".
  React.useEffect(() => {
    if (!isEdit) return;
    if (!editEvent?.id) { setAlertsState('ready'); return; }
    let alive = true;
    apiClient.get(`/alerts/${editEvent.id}`)
      .then(res => {
        if (!alive) return;
        const rows = res.data || [];
        const minutes = rows.map(a => a.minutes_before);
        if (minutes.length > 0) {
          setAlertSet(new Set(minutes));
          // All reminders for one event share the same type — read from first row
          if (rows[0]?.alert_type) setAlertType(rows[0].alert_type);
        }
        setAlertsState('ready');
      })
      .catch(() => { if (alive) setAlertsState('failed'); });
    return () => { alive = false; };
  }, [isEdit, editEvent?.id]);

  function handleChange(e) {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  }

  // Called when Whisper returns a transcript — send to GPT to fill the whole form
  async function handleVoiceTranscript(transcript) {
    sourceRef.current = 'voice';
    setParsing(true);
    setParseError(false);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const subCalendarsPayload = subCalendars.map(sc => ({ id: sc.id, name: sc.name, emoji: sc.emoji }));
      const groupsPayload = userGroups.map(g => ({ id: g.id, name: g.name }));
      const res = await apiClient.post('/parse-event', { transcript, timezone, subCalendars: subCalendarsPayload, groups: groupsPayload });
      const parsed = res.data;

      applyParsedToForm(parsed);

      // Only dictation can ask for these — a photo of a poster never says
      // "make it ring" or "put it on the family calendar".
      if (parsed.reminderAlarm === true) setAlertType('alarm');
      if (parsed.groupId && userGroups.find(g => g.id === parsed.groupId)) {
        setVisibility('group');
        setSelectedGroupId(parsed.groupId);
      }
    } catch (err) {
      console.error('Parse event error:', err);
      // 'limit' = Free-plan monthly AI allowance used up → friendly message
      setParseError(err.response?.status === 429 && err.response?.data?.code === 'AI_LIMIT_REACHED' ? 'limit' : true);
    } finally {
      setParsing(false);
    }
  }

  // Apply ONE parsed event (from voice OR photo) into the form fields.
  // There used to be two of these, near-identical, drifting apart in ways
  // nobody had decided: dictating an event put the detail in the TITLE while
  // photographing the same event put it in the NOTES, and only the dictated one
  // could turn "all day" back off. Same event, two behaviours.
  function applyParsedToForm(parsed) {
    const validSubCalId = parsed.subCalendarId &&
      subCalendars.find(sc => sc.id === parsed.subCalendarId)
      ? parsed.subCalendarId : null;
    setForm((f) => ({
      ...f,
      // The title stays SHORT: just what the event is. The extra detail goes to
      // the notes — not hidden there, the card shows it right under the title.
      title:         parsed.title    || f.title,
      type:          parsed.type     || f.type,
      datetime:      parsed.datetime ? isoToLocalInput(parsed.datetime) : f.datetime,
      location:      parsed.location || f.location,
      priority:      parsed.priority || f.priority,
      subCalendarId: validSubCalId   || f.subCalendarId,
      notes: parsed.notes
        ? (f.notes ? f.notes + ' ' + parsed.notes : parsed.notes)
        : f.notes,
      recurrence: parsed.recurrence
        ? (() => {
            const WD = ['SU','MO','TU','WE','TH','FR','SA'];
            const namedDays = Array.isArray(parsed.recurrenceByDay)
              ? parsed.recurrenceByDay.filter(d => WD.includes(d)) : [];
            return {
              ...f.recurrence,
              freq: parsed.recurrence,
              interval: parsed.recurrenceInterval > 1 ? parsed.recurrenceInterval : 1,
              byDay: parsed.recurrence === 'WEEKLY'
                ? (namedDays.length ? namedDays : [WD[new Date(parsed.datetime || f.datetime).getDay()]])
                : f.recurrence.byDay,
              monthlyMode: parsed.recurrence === 'MONTHLY' && parsed.recurrenceMonthlyMode === 'weekday' ? 'weekday' : 'day',
              endType: parsed.recurrenceCount ? 'count' : (parsed.recurrenceUntil ? 'until' : f.recurrence.endType),
              count:   parsed.recurrenceCount || f.recurrence.count,
              until:   parsed.recurrenceUntil || f.recurrence.until,
            };
          })()
        : f.recurrence,
      endDatetime:   parsed.endDatetime ? isoToLocalInput(parsed.endDatetime) : f.endDatetime,
      // An explicit "false" must be able to switch all-day back OFF, not just
      // "true" to switch it on.
      allDay:        parsed.allDay === true ? true : (parsed.allDay === false ? false : f.allDay),
    }));
    if (parsed.reminderMinutes != null) {
      setAlertSet(prev => new Set([...prev, parsed.reminderMinutes]));
    }
  }

  // Shared pipeline: compress → base64 → send to API → update form
  async function processImageBlob(blob) {
    sourceRef.current = 'photo';
    setImageParsing(true);
    setImageError(false);
    // Kept so that "crop and try again" does not mean taking the photo again.
    lastScanBlobRef.current = blob;
    try {
      const compressed = await compressImage(blob);
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(compressed);
      });
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await apiClient.post('/parse-image', {
        imageBase64: base64,
        mimeType: 'image/jpeg',
        timezone,
      });
      const events = Array.isArray(res.data?.events) ? res.data.events
        : (res.data && typeof res.data === 'object' ? [res.data] : []);
      if (events.length === 0) { setImageError(true); return; }
      track('photo_used'); // analytics: photo scanned (no image/content sent)
      if (events.length > 1) {
        setMultiEvents(events);       // several events → review screen (create all)
      } else {
        applyParsedToForm(events[0]); // single event → fill the form as usual
      }
    } catch (err) {
      console.error('Image parse error:', err);
      setImageError(err.response?.status === 429 && err.response?.data?.code === 'AI_LIMIT_REACHED' ? 'limit' : true);
    } finally {
      setImageParsing(false);
    }
  }

  // Gallery picker — show rotate/crop modal (same as camera, so user can crop)
  /**
   * Get a photo ready to look at.
   *
   * Shrink FIRST, then show. A camera photo is around 4032×3024 and the
   * browser must decode all of it before it can paint any of it — which is
   * where the five to ten seconds of dark screen came from. compressImage
   * asks for the small version up front, so there is something to look at
   * almost at once.
   *
   * The shrunk copy is also what gets scanned, so the shrinking that used to
   * happen again after the OK button now does not happen at all.
   */
  async function preparePhoto(file) {
    setPreparingPhoto(true);
    await paint();                       // let the dots be drawn before we block
    let blob = file;
    try {
      blob = await compressImage(file);
    } catch (_) {
      blob = file;                       // better a slow preview than none
    }
    const dataUrl = URL.createObjectURL(blob);
    setPhotoPreview({ blob, dataUrl, rotation: 0, mode: 'rotate', rotatedBlob: null, ready: true });
  }

  async function handleImageScan(e) {
    const file = e.target.files?.[0];
    if (!file) { setPreparingPhoto(false); return; }
    e.target.value = '';
    await preparePhoto(file);
  }

  // Camera capture — show rotate/crop preview first
  async function handleCameraCapture(e) {
    const file = e.target.files?.[0];
    if (!file) { setPreparingPhoto(false); return; }
    e.target.value = '';
    await preparePhoto(file);
  }

  // The photo is on screen: the dots have handed over to the preview.
  React.useEffect(() => { if (photoPreview) setPreparingPhoto(false); }, [photoPreview]);

  function cancelPhotoPreview() {
    if (photoPreview?.dataUrl) URL.revokeObjectURL(photoPreview.dataUrl);
    setPhotoPreview(null);
  }

  // FIX: Compress BEFORE rotating to avoid OOM when drawing large phone photos to canvas.
  async function applyRotationAndScan() {
    if (scanning) return;                       // one press is enough
    const { blob, dataUrl, rotation } = photoPreview;

    // The preview STAYS UP while this runs, and the button that was pressed
    // says it is working. Shrinking, turning and reading a 4032×3024 photo is
    // several seconds on a phone; it used to happen with the preview already
    // dismissed, behind a thin strip at the top of a form nobody was looking
    // at any more. Silvia: "niente fa capire che l'app stia lavorando mentre
    // con prova il pulsante si modifica e si vede che lavora." Exactly — so
    // this button does the same.
    setScanning(true);
    setImageParsing(true);
    setImageError(false);
    await paint();

    try {
      // Already shrunk when the preview was built — see preparePhoto. Doing it
      // again would be a second pass over the same picture for nothing.
      const rotated = await rotateBlob(blob, rotation);
      await processImageBlob(rotated);
    } finally {
      // Only now does the photo go away: closing it earlier is what made the
      // work invisible.
      URL.revokeObjectURL(dataUrl);
      setPhotoPreview(null);
      setScanning(false);
    }
  }

  // Enter crop mode: physically bake in the rotation, then show the crop overlay UI.
  async function enterCropMode() {
    const { blob, rotation } = photoPreview;
    setPhotoPreview(p => (p ? { ...p, mode: 'loading' } : p));   // spinner while processing
    await paint();                                               // ...and let it be drawn
    // A safety net: canvas work on a big photo can simply never call back on a
    // phone that is short of memory, and the spinner had no way out and no
    // button — a black screen with an hourglass, for good.
    const bail = new Promise(resolve => setTimeout(() => resolve(null), 15000));
    const work = (async () => {
      // Already small: preparePhoto shrank it before the preview was drawn.
      return rotation !== 0 ? await rotateBlob(blob, rotation) : blob;
    })();
    const rotated = await Promise.race([work, bail]);
    if (!rotated) {
      setPhotoPreview(p => (p ? { ...p, mode: 'rotate' } : p));  // back to a screen with buttons
      setImageError(true);
      return;
    }
    const newDataUrl = URL.createObjectURL(rotated);
    setCropRect({ x1: 0.1, y1: 0.1, x2: 0.9, y2: 0.9 });
    setPhotoPreview(p => {
      if (!p) { URL.revokeObjectURL(newDataUrl); return p; }     // user closed it meanwhile
      return {
        blob,                  // keep original File for "Back" navigation
        dataUrl: newDataUrl,   // shows the physically-rotated image
        rotation: 0,           // rotation is baked in
        mode: 'crop',
        rotatedBlob: rotated,
      };
    });
  }

  // Go back from crop mode to rotate mode (shows original, resets rotation to 0).
  function backToRotateMode() {
    const { blob, dataUrl } = photoPreview;
    URL.revokeObjectURL(dataUrl);                        // revoke the rotated image URL
    const originalUrl = URL.createObjectURL(blob);
    setPhotoPreview({ blob, dataUrl: originalUrl, rotation: 0, mode: 'rotate', rotatedBlob: null });
    setCropRect({ x1: 0.1, y1: 0.1, x2: 0.9, y2: 0.9 });
  }

  // Apply the crop selection and send to the API.
  // The preview STAYS UP while this runs, exactly like applyRotationAndScan:
  // closing it at once left nothing on screen that said the app was working,
  // and the press looked like it had not been taken.
  async function applyCropAndScan() {
    if (scanning) return;                       // one press is enough
    const { rotatedBlob, dataUrl } = photoPreview;
    setScanning(true);
    setImageParsing(true);
    setImageError(false);
    await paint();
    try {
      const cropped = await cropBlob(rotatedBlob, cropRect);
      await processImageBlob(cropped);
    } finally {
      URL.revokeObjectURL(dataUrl);
      setPhotoPreview(null);
      setScanning(false);
    }
  }

  // Re-open the LAST scanned image straight in crop mode. The scanner usually
  // fails because it was given the whole page instead of the one line that
  // matters, so "frame it better" is the useful next move — and it was one the
  // error message never mentioned.
  function retryScanWithCrop() {
    const blob = lastScanBlobRef.current;
    if (!blob) return;
    setImageError(false);
    setCropRect({ x1: 0.1, y1: 0.1, x2: 0.9, y2: 0.9 });
    setPhotoPreview({
      blob,
      dataUrl: URL.createObjectURL(blob),
      rotation: 0,
      mode: 'crop',
      rotatedBlob: blob,
    });
  }

  // ── Crop drag handlers ──────────────────────────────────────────────────────
  // draggingRef is a ref (not state) so the move handler always sees the
  // current value — state updates are async and the first move would see null.
  //
  // The old handlers listened only on the four corner circles: a finger landing
  // a few millimetres outside a circle grabbed nothing, which read as "the
  // handles are stuck". Now the WHOLE image listens — a touch takes the nearest
  // corner or edge within reach, and a touch inside the rectangle drags the
  // rectangle itself. Pointer capture keeps the drag alive even when the finger
  // wanders past the photo's border.
  const MIN_CROP = 0.08; // minimum crop dimension (8% of image)

  function cropPointFromEvent(e) {
    const rect = previewContainerRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (e.clientY - rect.top)  / rect.height)),
      rect,
    };
  }

  function handleCropPointerDown(e) {
    if (!previewContainerRef.current) return;
    e.preventDefault();
    const { x, y, rect } = cropPointFromEvent(e);
    const px = x * rect.width, py = y * rect.height;
    const cr = cropRect;
    const GRAB = 34;               // how far off a handle a touch may still land
    let drag = null;

    // Nearest corner within reach wins…
    let best = GRAB;
    for (const [mode, cx, cy] of [
      ['tl', cr.x1, cr.y1], ['tr', cr.x2, cr.y1],
      ['bl', cr.x1, cr.y2], ['br', cr.x2, cr.y2],
    ]) {
      const d = Math.hypot(px - cx * rect.width, py - cy * rect.height);
      if (d <= best) { best = d; drag = { mode }; }
    }

    // …then the nearest edge (moves one side on its own)…
    if (!drag) {
      const alongY = py >= cr.y1 * rect.height - GRAB && py <= cr.y2 * rect.height + GRAB;
      const alongX = px >= cr.x1 * rect.width  - GRAB && px <= cr.x2 * rect.width  + GRAB;
      let bestE = GRAB;
      for (const [mode, dist, along] of [
        ['l', Math.abs(px - cr.x1 * rect.width),  alongY],
        ['r', Math.abs(px - cr.x2 * rect.width),  alongY],
        ['t', Math.abs(py - cr.y1 * rect.height), alongX],
        ['b', Math.abs(py - cr.y2 * rect.height), alongX],
      ]) {
        if (along && dist <= bestE) { bestE = dist; drag = { mode }; }
      }
    }

    // …and a touch inside the rectangle moves the rectangle whole.
    if (!drag && x > cr.x1 && x < cr.x2 && y > cr.y1 && y < cr.y2) {
      drag = { mode: 'move', x0: x, y0: y, rect0: { ...cr } };
    }

    if (!drag) return;
    draggingRef.current = drag;
    try { previewContainerRef.current.setPointerCapture(e.pointerId); } catch (_) {}
  }

  function handleCropPointerMove(e) {
    const drag = draggingRef.current;
    if (!drag || !previewContainerRef.current) return;
    e.preventDefault();
    const { x, y } = cropPointFromEvent(e);
    setCropRect(cr => {
      switch (drag.mode) {
        case 'tl': return { ...cr, x1: Math.min(x, cr.x2 - MIN_CROP), y1: Math.min(y, cr.y2 - MIN_CROP) };
        case 'tr': return { ...cr, x2: Math.max(x, cr.x1 + MIN_CROP), y1: Math.min(y, cr.y2 - MIN_CROP) };
        case 'bl': return { ...cr, x1: Math.min(x, cr.x2 - MIN_CROP), y2: Math.max(y, cr.y1 + MIN_CROP) };
        case 'br': return { ...cr, x2: Math.max(x, cr.x1 + MIN_CROP), y2: Math.max(y, cr.y1 + MIN_CROP) };
        case 'l':  return { ...cr, x1: Math.min(x, cr.x2 - MIN_CROP) };
        case 'r':  return { ...cr, x2: Math.max(x, cr.x1 + MIN_CROP) };
        case 't':  return { ...cr, y1: Math.min(y, cr.y2 - MIN_CROP) };
        case 'b':  return { ...cr, y2: Math.max(y, cr.y1 + MIN_CROP) };
        case 'move': {
          const w = drag.rect0.x2 - drag.rect0.x1;
          const h = drag.rect0.y2 - drag.rect0.y1;
          const nx1 = Math.max(0, Math.min(1 - w, drag.rect0.x1 + (x - drag.x0)));
          const ny1 = Math.max(0, Math.min(1 - h, drag.rect0.y1 + (y - drag.y0)));
          return { x1: nx1, y1: ny1, x2: nx1 + w, y2: ny1 + h };
        }
        default: return cr;
      }
    });
  }

  function handleCropPointerEnd() {
    draggingRef.current = null;
  }

  function toggleAlert(value) {
    setAlertSet(prev => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }

  // Write the reminders for an event that has just been saved.
  // Deletes first (awaited), then adds: firing both at once let the DELETE land
  // after the POSTs and wipe the reminders we had just written, so the alarm
  // silently never rang. Returns true when they are all safely stored.
  // forceDelete is used when RETRYING: some of the POSTs may have gone through
  // the first time, and re-posting the whole set would double every reminder.
  async function syncAlerts({ googleEventId, eventStartIso, title, seriesAlert, forceDelete = false }) {
    // If we never managed to READ this event's reminders, we must not write
    // them either: alertSet would be an empty guess and the DELETE would throw
    // away reminders the user has never even seen.
    if (!googleEventId || alertsState === 'failed') return true;
    let ok = true;
    if (isEdit || alertSet.size === 0 || forceDelete) {
      await apiClient.delete(`/alerts/${googleEventId}`)
        // Removing the last reminder is itself a change the user asked for —
        // if the DELETE failed, the old reminder would keep ringing.
        .catch(() => { if (alertSet.size === 0) ok = false; });
    }
    if (alertSet.size > 0) {
      const soundName = localStorage.getItem(ALARM_KEYS.sound) || 'campanella';
      const saveAlert = (mins) => apiClient.post('/alerts', {
        googleEventId,
        eventTitle:    title || '',
        eventStart:    eventStartIso,
        minutesBefore: mins,
        soundName,
        alertType,
        recurring:     seriesAlert,
      });
      let pending = [...alertSet];
      for (let attempt = 0; attempt < 2 && pending.length > 0; attempt++) {
        const results = await Promise.allSettled(pending.map(saveAlert));
        pending = pending.filter((_, i) => results[i].status === 'rejected');
      }
      if (pending.length > 0) ok = false;
    }
    return ok;
  }

  // Upload a list of picked files; returns the ones that did NOT make it, so
  // they can be retried without re-sending the ones that already arrived.
  async function uploadFiles(googleEventId, eventStartIso, files) {
    if (!googleEventId || files.length === 0) return [];
    const gid = (visibility === 'group' && selectedGroupId) ? selectedGroupId : null;
    const failed = [];
    let done = 0;
    for (const f of files) {
      try {
        await apiClient.post('/attachments', {
          googleEventId, eventStart: eventStartIso, groupId: gid,
          filename: f.name, mime: f.mime, dataBase64: f.dataBase64,
        });
      } catch (_) {
        failed.push(f);
      }
      done++;
      setSaveStage({ kind: 'files', done, total: files.length });
    }
    return failed;
  }

  // Core save — called either directly (non-recurring) or from the scope modal
  async function doSave(targetId, payload) {
    setSaving(true);
    setShowRecurringEditModal(false);
    setErrorKey(null);
    setApiError(null);
    setSaveStage({ kind: 'event' });
    try {
      // payload.datetime may be absent on a series-wide edit that kept the
      // original date/time — alerts then anchor to the opened occurrence.
      const eventStartIso = payload.datetime ||
        (editEvent?.start ? new Date(editEvent.start).toISOString() : null);
      let googleEventId;
      if (isEdit) {
        await apiClient.put(`/events/${targetId}`, payload);
        googleEventId = targetId;
      } else {
        const res = await apiClient.post('/events', payload);
        googleEventId = res.data?.id;
        // Analytics: a new event was created (no personal content sent — only
        // how it was made + safe metadata). Powers the activation funnel.
        track('event_created', {
          method:     sourceRef.current,        // voice | photo | manual
          priority:   payload.priority || null, // red/yellow/green
          shared:     payload.visibility === 'group',
          recurring:  !!payload.recurrenceRule,
        });
      }

      // `recurring: true` = this alert row is keyed to a whole SERIES (new event
      // with a rule, or an "entire series" edit): the server spares it from
      // cleanup and re-anchors it to each next occurrence.
      const seriesAlert = isEdit
        ? Boolean(editEvent?.recurringEventId && targetId === editEvent.recurringEventId)
        : Boolean(payload.recurrenceRule);

      setSaveStage({ kind: 'alerts' });
      const alertsOk = await syncAlerts({
        googleEventId, eventStartIso, title: payload.title, seriesAlert,
      });

      setSaveStage({ kind: 'files', done: 0, total: pendingFiles.length });
      const failedFiles = await uploadFiles(googleEventId, eventStartIso, pendingFiles);

      invalidateEventCache(); // Today must refetch, not show the stale cached list

      // Hand the new reminder to Android straight away.
      //
      // THIS IS LORAINE'S BUG. The native queue was only re-synced when the app
      // opened, and then not again for five minutes. So: set a reminder, leave
      // the app — and Android was never told. Nothing rang. Come back, and the
      // in-app scheduler noticed it was overdue and rang there and then. "It
      // did not go off. I went back into the app and it was going off."
      //
      // A reminder is the one thing this app must not be late with, and the
      // moment it is created is exactly when the system needs to hear about it.
      // No-op on the web, where Web Push already carries it.
      syncNativeAlarms(user?.language || 'it').catch(() => {});

      setSaveStage(null);

      // The event IS saved. If a secondary piece did not make it, stay here and
      // offer to retry just that piece. It used to be a system alert box saying
      // "open the event and save it again" — followed immediately by leaving the
      // form, so the user was told to do something and then shown the door.
      if (!alertsOk || failedFiles.length > 0) {
        setSaving(false);
        setPostSave({
          googleEventId, eventStartIso, title: payload.title, seriesAlert,
          alertsFailed: !alertsOk, failedFiles,
        });
        return;
      }
      leavingOnPurpose.current = true; navigate("/");
    } catch (err) {
      setApiError(describeSaveError(err));
      setErrorKey('save');
      setSaving(false);
      setSaveStage(null);
    }
  }

  // Retry only the part that failed, against the event that already exists —
  // never the event itself, so this can't create a twin.
  async function retryPostSave() {
    if (!postSave) return;
    setRetrying(true);
    const ps = postSave;
    let alertsOk = true;
    if (ps.alertsFailed) {
      alertsOk = await syncAlerts({
        googleEventId: ps.googleEventId, eventStartIso: ps.eventStartIso,
        title: ps.title, seriesAlert: ps.seriesAlert,
        forceDelete: true,   // clean slate: don't double the ones that got through
      }).catch(() => false);
    }
    const stillFailed = ps.failedFiles.length > 0
      ? await uploadFiles(ps.googleEventId, ps.eventStartIso, ps.failedFiles)
      : [];
    setRetrying(false);
    setSaveStage(null);
    if (alertsOk && stillFailed.length === 0) {
      invalidateEventCache();
      setPostSave(null);
      leavingOnPurpose.current = true; navigate("/");
      return;
    }
    setPostSave({ ...ps, alertsFailed: !alertsOk, failedFiles: stillFailed });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.allDay && form.datetime && !hasTime(form.datetime)) {
      setErrorKey('timeMissing');
      return;
    }
    if (!form.title || !form.datetime || !form.priority) {
      setErrorKey('required');
      // "Required fields" while the title is clearly filled in left people
      // hunting: say WHICH one, when we know.
      setApiError(!form.title ? null : SE.noDate);
      revealMissingField(!form.title ? 'title' : 'date');
      return;
    }

    // Guard the ISO conversion: a malformed datetime would otherwise throw here
    // (outside any try/catch) and abort the submit with no visible reason.
    let eventStartIso, endIso;
    try {
      // An all-day event can now reach here with a bare date and no hour, and
      // `new Date('2026-08-20')` is midnight UTC — which is the evening of the
      // 19th for anyone west of London. Local midday cannot fall off either
      // end of the day, whatever the timezone.
      const startLocal = hasTime(form.datetime) ? form.datetime : form.datetime + 'T12:00';
      eventStartIso = new Date(startLocal).toISOString();
      endIso = form.endDatetime ? new Date(form.endDatetime).toISOString() : undefined;
    } catch (_) {
      setApiError(SE.noDate);
      setErrorKey('required');
      revealMissingField('date');   // it is the date that is unusable, not the title
      return;
    }
    // Never send an end that is <= start: Google/Microsoft reject it with a
    // cryptic "invalid start time". Drop it so the server defaults end = start + 1h.
    if (endIso && new Date(endIso).getTime() <= new Date(eventStartIso).getTime()) {
      endIso = undefined;
    }
    const payload = {
      ...form,
      datetime: eventStartIso,
      endDatetime: endIso,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      recurrenceRule: buildRRule(form.recurrence, form.datetime),
      alertMinutesArray: alertSet.size > 0 ? [...alertSet] : null,
      // This used to read `userGroups.length > 0 ? visibility : 'private'`, so a
      // save that beat the (separate, slower) GET /groups request quietly turned
      // a shared event into a private one. What decides is the user's own
      // choice plus a group actually being selected — nothing else.
      visibility: (visibility === 'group' && selectedGroupId) ? 'group' : 'private',
      groupId:    (visibility === 'group' && selectedGroupId) ? selectedGroupId : null,
      source: sourceRef.current,   // manual / voice / photo — for launch analytics
    };

    // Overlapping events are allowed (like Google Calendar): saving is instant,
    // never blocked or delayed by a pre-save conflict check. Shared-calendar
    // clashes are still surfaced non-blockingly as you pick the time (below).

    // Recurring event in edit mode → ask user: this occurrence or entire series
    if (isEdit && editEvent?.recurringEventId) {
      setPendingPayload(payload);
      setShowRecurringEditModal(true);
      return;
    }

    await doSave(isEdit ? editId : null, payload);
  }

  // ── Delete (edit mode only) — mirrors EventCard's delete logic ────────────
  // Both cases go through the app's own modal now. A plain window.confirm for
  // ordinary events and a designed modal for recurring ones meant the same
  // action looked like two different apps depending on the event.
  function handleDelete() {
    setShowDeleteModal(editEvent?.recurringEventId ? 'recurring' : 'single');
  }

  async function doDelete(targetId) {
    setDeleting(true);
    setShowDeleteModal(null);
    setErrorKey(null);
    setApiError(null);
    try {
      await apiClient.delete(`/events/${targetId}`);
      invalidateEventCache(); // Today must refetch, not show the deleted event
      // And take the alarm off Android's queue with it: an alarm for an event
      // that no longer exists is worse than no alarm at all.
      syncNativeAlarms(user?.language || 'it').catch(() => {});
      leavingOnPurpose.current = true; navigate("/");
    } catch (err) {
      setDeleting(false);
      setApiError(describeSaveError(err));
      setErrorKey('delete');
    }
  }

  // The shell above never renders this without an event in edit mode, so there
  // is nothing left to guard here.

  // A photo with several events → show the review/confirm list (create all).
  if (multiEvents) {
    return (
      <MultiEventReview
        events={multiEvents}
        timezone={Intl.DateTimeFormat().resolvedOptions().timeZone}
        T={T.form}
        lang={lang}
        /* The calendars, and whichever one was already chosen on the form: a
           photograph taken while the family calendar was selected should not
           quietly land in the personal one. */
        groups={userGroups}
        userName={user?.name || ''}
        visibility={visibility}
        groupId={selectedGroupId}
        subCalendars={subCalendars}
        subCalendarId={form.subCalendarId}
        onCancel={() => setMultiEvents(null)}
        onCreated={() => { leavingOnPurpose.current = true; invalidateEventCache(); navigate("/"); }}
      />
    );
  }

  return (
    <>
      {/* ── Photo modal (rotate / crop) ──────────────────────────────────────── */}
      {photoPreview && (
        <DialogCard
          // This one needs a spoken name: the two headings inside are ordinary
          // paragraphs, and the rest of the screen is a photograph.
          label={photoPreview.mode === 'crop' ? PL.cropTitle : PL.title}
          style={{
          position: 'fixed', inset: 0, zIndex: 300,
          background: 'rgba(0,0,0,0.88)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: '24px 20px',
        }}>

          {/* Always a way out, in every mode. Crop mode used to offer only
              "Back" and "Scan": to abandon a photo opened by mistake you had to
              step back first, and the phone's back button left the whole form
              instead, taking everything already typed with it. */}
          <button
            type="button"
            onClick={cancelPhotoPreview}
            aria-label={PL.cancel}
            style={{
              position: 'absolute', top: 'calc(12px + env(safe-area-inset-top, 0px))',
              insetInlineEnd: 12, zIndex: 2,
              width: 44, height: 44, borderRadius: '50%',
              // Green like every other way out, not a pale cross floating on
              // the photograph: over an image a control has to look like a
              // control.
              background: 'var(--color-primary)',
              border: 'none',
              color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          ><span className="rtl-flip" style={{ display: 'flex' }}><svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" stroke="currentColor" fill="none" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg></span></button>

          {/* ── Loading state (entering crop mode) ───────────────────────── */}
          {photoPreview.mode === 'loading' && (
            <div style={{ color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
              <span style={{ fontSize: '2.2rem', animation: 'spin 0.8s linear infinite', display: 'inline-block' }}>⏳</span>
              <span style={{ fontWeight: 600, fontSize: '1rem', opacity: 0.8 }}>
                {PREPARING_LABELS[baseLang] || PREPARING_LABELS.en}
              </span>
            </div>
          )}

          {/* ── Crop mode ─────────────────────────────────────────────────── */}
          {photoPreview.mode === 'crop' && (
            <>
              <p style={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', marginBottom: 6, letterSpacing: '0.01em' }}>
                {PL.cropTitle}
              </p>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.76rem', marginBottom: 14, textAlign: 'center', maxWidth: 300 }}>
                {CROP_HINTS[baseLang] || CROP_HINTS.en}
              </p>

              {/* Image + drag-to-crop overlay */}
              <div
                ref={previewContainerRef}
                onPointerDown={handleCropPointerDown}
                onPointerMove={handleCropPointerMove}
                onPointerUp={handleCropPointerEnd}
                onPointerCancel={handleCropPointerEnd}
                style={{
                  position: 'relative', display: 'inline-block', lineHeight: 0,
                  marginBottom: 20, borderRadius: 6, overflow: 'hidden',
                  touchAction: 'none', userSelect: 'none',
                }}
              >
                <img
                  src={photoPreview.dataUrl}
                  alt="crop"
                  style={{ maxWidth: 'min(88vw, 480px)', maxHeight: '50vh', display: 'block' }}
                  draggable={false}
                />

                {/* Dark strips outside the crop rectangle */}
                <div style={{ position:'absolute', top:0, left:0, right:0,
                  height:`${cropRect.y1*100}%`, background:'rgba(0,0,0,0.58)', pointerEvents:'none' }} />
                <div style={{ position:'absolute', bottom:0, left:0, right:0,
                  height:`${(1-cropRect.y2)*100}%`, background:'rgba(0,0,0,0.58)', pointerEvents:'none' }} />
                <div style={{ position:'absolute', top:`${cropRect.y1*100}%`, bottom:`${(1-cropRect.y2)*100}%`,
                  left:0, width:`${cropRect.x1*100}%`, background:'rgba(0,0,0,0.58)', pointerEvents:'none' }} />
                <div style={{ position:'absolute', top:`${cropRect.y1*100}%`, bottom:`${(1-cropRect.y2)*100}%`,
                  right:0, width:`${(1-cropRect.x2)*100}%`, background:'rgba(0,0,0,0.58)', pointerEvents:'none' }} />

                {/* Crop rectangle border */}
                <div style={{
                  position: 'absolute',
                  left:   `${cropRect.x1 * 100}%`,
                  top:    `${cropRect.y1 * 100}%`,
                  width:  `${(cropRect.x2 - cropRect.x1) * 100}%`,
                  height: `${(cropRect.y2 - cropRect.y1) * 100}%`,
                  border: '2px solid rgba(255,255,255,0.9)',
                  boxSizing: 'border-box', pointerEvents: 'none',
                }} />

                {/* Handles are visual markers only — the whole image listens
                    for the touch and grabs whatever is nearest (see
                    handleCropPointerDown), so a near miss still works. */}
                {[
                  ['tl', cropRect.x1, cropRect.y1],
                  ['tr', cropRect.x2, cropRect.y1],
                  ['bl', cropRect.x1, cropRect.y2],
                  ['br', cropRect.x2, cropRect.y2],
                ].map(([corner, x, y]) => (
                  <div
                    key={corner}
                    style={{
                      position: 'absolute',
                      left: `${x * 100}%`,
                      top:  `${y * 100}%`,
                      transform: 'translate(-50%, -50%)',
                      width: 30, height: 30, borderRadius: '50%',
                      background: '#fff',
                      border: '2.5px solid rgba(0,0,0,0.3)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.45)',
                      pointerEvents: 'none', zIndex: 2,
                    }}
                  />
                ))}
                {/* Mid-edge pills: each side can be dragged on its own */}
                {[
                  ['t', (cropRect.x1 + cropRect.x2) / 2, cropRect.y1, 26, 9],
                  ['b', (cropRect.x1 + cropRect.x2) / 2, cropRect.y2, 26, 9],
                  ['l', cropRect.x1, (cropRect.y1 + cropRect.y2) / 2, 9, 26],
                  ['r', cropRect.x2, (cropRect.y1 + cropRect.y2) / 2, 9, 26],
                ].map(([edge, x, y, w, h]) => (
                  <div
                    key={edge}
                    style={{
                      position: 'absolute',
                      left: `${x * 100}%`,
                      top:  `${y * 100}%`,
                      transform: 'translate(-50%, -50%)',
                      width: w, height: h, borderRadius: 5,
                      background: '#fff',
                      boxShadow: '0 1px 5px rgba(0,0,0,0.45)',
                      pointerEvents: 'none', zIndex: 2,
                    }}
                  />
                ))}
              </div>

              {/* Crop mode buttons: Back | Scan */}
              <div style={{ display: 'flex', gap: 12, width: '100%', maxWidth: 380 }}>
                <button
                  type="button"
                  onClick={backToRotateMode}
                  disabled={scanning}
                  style={{
                    flex: 1, padding: '14px 0', borderRadius: 10,
                    background: 'transparent',
                    border: '2px solid rgba(255,255,255,0.4)',
                    color: '#fff', fontWeight: 700, fontSize: '0.9rem',
                    cursor: scanning ? 'not-allowed' : 'pointer',
                    opacity: scanning ? 0.4 : 1,
                  }}
                >
                  {PL.back}
                </button>
                <button
                  type="button"
                  onClick={applyCropAndScan}
                  disabled={scanning}
                  aria-busy={scanning}
                  style={{
                    flex: 2, padding: '14px 0', borderRadius: 10,
                    background: scanning ? 'rgba(255,255,255,0.18)' : 'var(--color-primary)',
                    border: scanning ? '2px solid rgba(255,255,255,0.5)' : 'none',
                    color: '#fff', fontWeight: 700, fontSize: '0.95rem',
                    cursor: scanning ? 'wait' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                >
                  {scanning && (
                    <span style={{ animation: 'spin 0.8s linear infinite', display: 'inline-block' }}>⏳</span>
                  )}
                  {scanning ? (T.form.imageParsing || '…') : PL.scan}
                </button>
              </div>
            </>
          )}

          {/* ── Rotate mode ───────────────────────────────────────────────── */}
          {photoPreview.mode === 'rotate' && (
            <>
              <p style={{
                color: '#fff', fontWeight: 700, fontSize: '1.05rem',
                marginBottom: 16, letterSpacing: '0.01em',
              }}>
                {PL.title}
              </p>

              {/* Square image container — handles any rotation without overflow */}
              <div style={{
                width: 'min(72vw, 72vh)', height: 'min(72vw, 72vh)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 22, overflow: 'hidden',
              }}>
                <img
                  src={photoPreview.dataUrl}
                  alt="preview"
                  style={{
                    maxWidth: '100%', maxHeight: '100%',
                    objectFit: 'contain',
                    transform: `rotate(${photoPreview.rotation}deg)`,
                    transition: 'transform 0.25s ease',
                    borderRadius: 6,
                  }}
                />
              </div>

              {/* Rotate CCW / CW buttons */}
              <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
                {[
                  { deg: -90, icon: '↺', label: 'CCW' },
                  { deg: +90, icon: '↻', label: 'CW'  },
                ].map(({ deg, icon, label }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setPhotoPreview(p => ({
                      ...p,
                      rotation: (p.rotation + deg + 360) % 360,
                    }))}
                    style={{
                      width: 56, height: 56, borderRadius: '50%',
                      background: 'rgba(255,255,255,0.13)',
                      border: '2px solid rgba(255,255,255,0.45)',
                      color: '#fff', fontSize: '1.7rem', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      lineHeight: 1,
                    }}
                  >
                    <span style={{ lineHeight: 1 }}>{icon}</span>
                  </button>
                ))}
              </div>

              {/* Action buttons: Cancel | ✂️ Crop | 🔍 Scan */}
              <div style={{ display: 'flex', gap: 10, width: '100%', maxWidth: 380 }}>
                <button
                  type="button"
                  onClick={cancelPhotoPreview}
                  style={{
                    flex: 1, padding: '14px 0', borderRadius: 10,
                    background: 'transparent',
                    border: '2px solid rgba(255,255,255,0.4)',
                    color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
                  }}
                >
                  {PL.cancel}
                </button>
                <button
                  type="button"
                  onClick={enterCropMode}
                  style={{
                    flex: 1, padding: '14px 0', borderRadius: 10,
                    background: 'rgba(255,255,255,0.13)',
                    border: '2px solid rgba(255,255,255,0.4)',
                    color: '#fff', fontWeight: 700, fontSize: '0.82rem',
                    cursor: scanning ? 'not-allowed' : 'pointer',
                    opacity: scanning ? 0.4 : 1,
                  }}
                  disabled={scanning}
                >
                  {PL.crop}
                </button>
                <button
                  type="button"
                  onClick={applyRotationAndScan}
                  disabled={scanning}
                  aria-busy={scanning}
                  style={{
                    flex: 2, padding: '14px 0', borderRadius: 10,
                    background: scanning ? 'rgba(255,255,255,0.18)' : 'var(--color-primary)',
                    border: scanning ? '2px solid rgba(255,255,255,0.5)' : 'none',
                    color: '#fff', fontWeight: 700, fontSize: '0.95rem',
                    cursor: scanning ? 'wait' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                >
                  {scanning && (
                    <span style={{ animation: 'spin 0.8s linear infinite', display: 'inline-block' }}>⏳</span>
                  )}
                  {scanning ? (T.form.imageParsing || '…') : PL.scan}
                </button>
              </div>
            </>
          )}
        </DialogCard>
      )}

      {/* ── About to lose what you have written ─────────────────────────── */}
      {askDiscard && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 320,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}>
          <DialogCard style={{
            background: 'var(--color-card)', borderRadius: 'var(--radius)',
            padding: '22px 20px', width: '100%', maxWidth: 340,
            boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
          }}>
            <p style={{ margin: '0 0 18px', fontWeight: 700, fontSize: '0.95rem', color: 'var(--color-text)' }}>
              {(DISCARD_EVENT[baseLang] || DISCARD_EVENT.en).text}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Staying is the safe answer, so it comes first and is the plain one */}
              <button type="button" className="btn btn-outline" onClick={() => setAskDiscard(null)}>
                {(DISCARD_EVENT[baseLang] || DISCARD_EVENT.en).stay}
              </button>
              <button
                type="button"
                className="btn btn-outline"
                style={{ borderColor: 'var(--color-stripe-red)', color: 'var(--color-stripe-red)' }}
                onClick={() => {
                  leavingOnPurpose.current = true;
                  const go = askDiscard;
                  setAskDiscard(null);
                  go();
                }}
              >
                {(DISCARD_EVENT[baseLang] || DISCARD_EVENT.en).leave}
              </button>
            </div>
          </DialogCard>
        </div>
      )}

      {/* ── The event saved, something attached to it did not ───────────────
          Not a system alert box that gives an instruction and then closes the
          form anyway: the offer to retry is here, and it retries ONLY the part
          that failed — never the event, so this can't create a duplicate. */}
      {postSave && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 310,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}>
          <DialogCard style={{
            background: 'var(--color-card)', borderRadius: 'var(--radius)',
            padding: '22px 20px', width: '100%', maxWidth: 360,
            boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
          }}>
            {postSave.alertsFailed && (
              <p style={{ margin: '0 0 12px', fontSize: '0.88rem', lineHeight: 1.45, color: 'var(--color-text)' }}>
                ⚠️ {ALERTS_NOT_SAVED[baseLang] || ALERTS_NOT_SAVED.en}
              </p>
            )}
            {postSave.failedFiles.length > 0 && (
              <p style={{ margin: '0 0 12px', fontSize: '0.88rem', lineHeight: 1.45, color: 'var(--color-text)' }}>
                ⚠️ {(ATTACHMENTS_NOT_SAVED[baseLang] || ATTACHMENTS_NOT_SAVED.en)(postSave.failedFiles.length)}
              </p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
              <button
                type="button"
                className="btn btn-primary"
                disabled={retrying}
                onClick={retryPostSave}
              >
                {retrying ? '⏳' : RL.retry}
              </button>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={retrying}
                onClick={() => { leavingOnPurpose.current = true; setPostSave(null); navigate("/"); }}
              >
                {RL.leave}
              </button>
            </div>
          </DialogCard>
        </div>
      )}

      {/* ── Recurring-edit scope modal ──────────────────────────────────── */}
      {/* NOTE: tapping the dark backdrop does NOT close this one. The user has
          already pressed OK; the modal is the last step of a save in progress.
          A stray thumb on the backdrop used to dismiss it and quietly throw the
          save away — the form looked untouched and the change was simply lost.
          Leaving is done through the explicit Cancel button below. */}
      {showRecurringEditModal && pendingPayload && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
        >
          <DialogCard
            style={{
              background: 'var(--color-card)', borderRadius: 'var(--radius)',
              padding: '24px 20px', width: '100%', maxWidth: 340,
              boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
            }}
          >
            <p style={{ margin: '0 0 18px', fontWeight: 700, fontSize: '0.95rem', color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ display: 'inline-flex', width: 20, height: 20, borderRadius: '50%', background: 'var(--color-primary)', color: '#fff', fontSize: '0.7rem', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>↻</span>
              {T.form.recurringEditTitle}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* This occurrence only */}
              <button className="btn btn-outline" onClick={() => doSave(editId, pendingPayload)}>
                {T.form.recurringThisOnly}
              </button>
              {/* Entire series */}
              <button
                className="btn btn-primary"
                onClick={() => {
                  // If date/time were NOT touched, drop them from the payload:
                  // the server then patches the series WITHOUT start/end, so the
                  // series keeps its original anchor (M5). If the user DID
                  // change them, send them — that's an intentional reschedule.
                  const p = { ...pendingPayload };
                  const init = initialTimeRef.current;
                  const iso = (v) => { try { return v ? new Date(v).toISOString() : null; } catch (_) { return null; } };
                  const untouched =
                    p.datetime === iso(init.datetime) &&
                    (p.endDatetime || null) === iso(init.endDatetime) &&
                    p.allDay === init.allDay;
                  if (untouched) { delete p.datetime; delete p.endDatetime; delete p.allDay; }
                  doSave(editEvent.recurringEventId, p);
                }}
              >
                {T.form.recurringAllSeries}
              </button>
              {/* Cancel */}
              <button className="btn btn-outline btn-sm" onClick={() => setShowRecurringEditModal(false)}>
                {T.form.cancelButton}
              </button>
            </div>
          </DialogCard>
        </div>
      )}

      {/* ── Recurring-delete scope modal ────────────────────────────────── */}
      {showDeleteModal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
          onClick={() => setShowDeleteModal(null)}
        >
          <DialogCard
            style={{
              background: 'var(--color-card)', borderRadius: 'var(--radius)',
              padding: '24px 20px', width: '100%', maxWidth: 340,
              boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <p style={{ margin: '0 0 18px', fontWeight: 700, fontSize: '0.95rem', color: 'var(--color-text)' }}>
              {showDeleteModal === 'recurring' ? `🔁 ${T.form.deleteRecurring}` : T.form.deleteConfirm}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {showDeleteModal === 'recurring' ? (
                <>
                  {/* This occurrence only */}
                  <button className="btn btn-outline" onClick={() => doDelete(editId)}>
                    {T.form.deleteOnlyThis}
                  </button>
                  {/* Entire series */}
                  <button
                    className="btn btn-outline"
                    style={{ borderColor: 'var(--color-red)', color: 'var(--color-red)' }}
                    onClick={() => doDelete(editEvent.recurringEventId)}
                  >
                    {T.form.deleteAllSeries}
                  </button>
                </>
              ) : (
                <button
                  className="btn btn-outline"
                  style={{ borderColor: 'var(--color-red)', color: 'var(--color-red)' }}
                  onClick={() => doDelete(editId)}
                >
                  {T.form.deleteButton}
                </button>
              )}
              {/* Cancel */}
              <button className="btn btn-outline btn-sm" onClick={() => setShowDeleteModal(null)}>
                {T.form.cancelButton}
              </button>
            </div>
          </DialogCard>
        </div>
      )}

      {/* ── Main form ───────────────────────────────────────────────────── */}
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%', paddingBottom: 0 }}>
        {/* Scrollable content wrapper — contains all form fields */}
        <div className="no-scrollbar" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingBottom: isEdit ? 80 : 140 }}>
          {/* The way out.
              A tester: "If I accidentally start to create an event how do I get
              out of it? It is not letting me go backwards but only hit okay but
              I don't actually want to create the event. I don't think there's a
              cancel button." There wasn't. Tapping ＋ by mistake put you in a
              screen whose only visible action was to go through with it — the
              phone's back button worked, but nothing on screen said so, and
              somebody who is not sure what an app will do does not experiment.
              It sits top-LEFT, as far from OK as the row allows: the two
              opposite decisions must not be neighbours. Everything already
              needed was here — the same question the bottom bar asks when you
              wander off with unsaved work. It just had no button. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: isEdit ? 0 : 4 }}>
            <button
              type="button"
              onClick={handleClose}
              aria-label={A.back}
              title={A.back}
              className="tap-grow"
              style={{
                '--tap-x': '6px',
                width: 38, height: 38, flexShrink: 0, padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: '50%', cursor: 'pointer',
                background: 'var(--color-primary)',
                border: 'none',
                color: '#fff',
              }}
            ><span className="rtl-flip" style={{ display: "flex" }}><BackIcon /></span></button>
            {/* Six icons, right where the eye already is. Tapping one puts it at
                the front of the title — so it travels with the event into the
                summary, into Google, onto somebody else shared calendar. */}
            {!readOnly && (
              <IconPicker
                value={form.title}
                language={lang}
                onChange={(t) => setForm(f => ({ ...f, title: t }))}
              />
            )}
            {isEdit && <h2 className="page-title" style={{ margin: 0 }}>{T.form.editPageTitle}</h2>}
            {clearedDraft && (
              <button
                type="button"
                onClick={undoClear}
                style={{
                  marginInlineStart: 'auto',
                  background: 'none', border: 'none', padding: '6px 4px',
                  color: 'var(--color-primary)', fontFamily: 'inherit',
                  fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                {(UNDO_CLEAR[baseLang] || UNDO_CLEAR.en)}
              </button>
            )}
          </div>

          {/* Parsing / image status — inline banner at top of form */}
        {(parsing || imageParsing) && (
          <div style={{
            padding: '10px 12px', marginBottom: 8,
            background: 'var(--color-surface)', borderRadius: 'var(--radius-sm)',
            fontSize: '0.85rem', color: 'var(--color-text-muted)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ animation: 'spin 0.8s linear infinite', display: 'inline-block' }}>⏳</span>
            {imageParsing ? T.form.imageParsing : T.form.parsing}
          </div>
        )}
        {/* A failed scan / a misheard dictation now says what to do next, with
            the button to do it. "I didn't understand" on its own left the user
            to work out on their own that framing the photo would help. */}
        {parseError && (
          <div className="alert alert-error" style={{ marginBottom: 8 }}>
            {parseError === 'limit'
              ? (canShowPurchase()
                  ? <a href="/settings?upgrade=1" style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: '3px' }}>{T.today.aiLimitReached} ⭐</a>
                  // In the Play Store app: state the fact, offer no way to buy.
                  : <span>{T.today.aiLimitReached}</span>)
              : (
                <>
                  <div>{T.form.parseError}</div>
                  <button type="button" className="btn btn-outline btn-sm" style={{ marginTop: 8 }}
                    onClick={() => { setParseError(false); handleMainMicToggle(); }}>
                    {RL.again}
                  </button>
                </>
              )}
          </div>
        )}
        {imageError && (
          <div className="alert alert-error" style={{ marginBottom: 8 }}>
            {imageError === 'limit'
              ? (canShowPurchase()
                  ? <a href="/settings?upgrade=1" style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: '3px' }}>{T.today.aiLimitReached} ⭐</a>
                  // In the Play Store app: state the fact, offer no way to buy.
                  : <span>{T.today.aiLimitReached}</span>)
              : (
                <>
                  <div>{T.form.imageError}</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    {lastScanBlobRef.current && (
                      <button type="button" className="btn btn-outline btn-sm" onClick={retryScanWithCrop}>
                        {RL.crop}
                      </button>
                    )}
                    <button type="button" className="btn btn-outline btn-sm"
                      onClick={() => { setImageError(false); cameraInputRef.current?.click(); }}>
                      {RL.another}
                    </button>
                  </div>
                </>
              )}
          </div>
        )}

        {errorKey && (
          <div className="alert alert-error" role="alert" aria-live="assertive">
            {apiError || (errorKey === 'timeMissing' ? T.form.timeMissing : errorKey === 'required' ? T.form.required
              : errorKey === 'delete' ? T.form.deleteError
              : T.form.saveError)}
          </div>
        )}

        {/* ── Title ────────────────────────────────────────────────────── */}
        <div style={{ padding: '18px 0 14px' }}>
          <textarea
            ref={titleRef}
            className="form-input-title"
            name="title"
            value={stripIcon(form.title)}
            onChange={e => setForm(f => ({ ...f, title: withIcon(e.target.value, iconOf(f.title)) }))}
            placeholder={T.form.titlePlaceholder}
            aria-label={T.form.titlePlaceholder}
            required
            disabled={readOnly}
            rows={1}
            // Red underline on the field itself, so the reason is visible where
            // the user is looking — not only in the banner at the top.
            style={errorKey === 'required' && !form.title
              ? { boxShadow: 'inset 0 -2px 0 #e63946' }
              : undefined}
          />
        </div>

        <Divider />

        {/* ── Date & Time ──────────────────────────────────────────────── */}
        <div style={{ padding: '14px 0' }}>
          {/* Start on the left, end on the right, an arrow between them, and the
              end ALWAYS THERE — the shape Silvia sent from Google, and she is
              right that it is cleaner. What it replaces was a single row plus a
              "FINO A" button that had to be discovered before an event could be
              given a length; and once pressed, the row it added had a heading
              of its own, so the form suddenly grew two labels it did not have a
              moment before.

              The end shown when nobody has set one is start + 1 hour, and that
              is not a guess: it is exactly what the server already does with an
              event that arrives without an end (buildTimeFields). So the form
              now SHOWS what was always going to happen instead of leaving it
              blank and deciding later.

              It is shown but not stored. form.endDatetime stays empty until it
              is actually changed, which keeps the "untouched" check on save
              working as before — editing an old event still leaves its end
              alone unless you touch it. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
              <input
                ref={dateRef}
                type="date"
                className="form-input-flat"
                style={{ fontWeight: 700 }}
                value={form.datetime ? form.datetime.split('T')[0] : ''}
                onChange={(e) => {
                  // Keep the hour if there is one; do not invent one if there is not.
                  const time = form.datetime?.split('T')[1]?.slice(0, 5) || '';
                  setForm(f => ({ ...f, datetime: e.target.value + (time ? 'T' + time : '') }));
                }}
                disabled={readOnly}
              />
              {!form.allDay && (
                <TimeField
                  disabled={readOnly}
                  hour12={uses12h(lang)}
                  value={form.datetime ? (form.datetime.split('T')[1]?.slice(0, 5) || '') : ''}
                  label={T.form.timeMissing}
                  onChange={(t) => {
                    const date = form.datetime?.split('T')[0] || todayLocalISO().split('T')[0];
                    setTimeChosen(true);
                    setForm(f => ({ ...f, datetime: date + 'T' + t }));
                  }}
                />
              )}
            </div>

            <span className="rtl-flip" aria-hidden="true" style={{ flexShrink: 0, color: 'var(--color-text-muted)', display: 'flex' }}>
              <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" /><path d="M13 5l7 7-7 7" />
              </svg>
            </span>

            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
              <input
                type="date"
                className="form-input-flat"
                style={{ fontWeight: 700 }}
                value={endShown.slice(0, 10)}
                onChange={(e) => {
                  const time = endShown.slice(11, 16);
                  setForm(f => ({ ...f, endDatetime: e.target.value + 'T' + time }));
                }}
                disabled={readOnly}
              />
              {!form.allDay && (
                <TimeField
                  disabled={readOnly}
                  hour12={uses12h(lang)}
                  value={endShown.slice(11, 16)}
                  onChange={(t) => {
                    setForm(f => ({ ...f, endDatetime: endShown.slice(0, 10) + 'T' + t }));
                  }}
                />
              )}
            </div>
          </div>

          {/* All-day toggle (left) + edit-mode action icons (right) */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 14 }}>
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, allDay: !f.allDay }))}
              disabled={readOnly}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: 0,
                background: 'none', border: 'none',
                cursor: readOnly ? 'not-allowed' : 'pointer',
                opacity: readOnly ? 0.55 : 1,
              }}
            >
{/* A box you tick, at Silvia's request, in place of the sliding switch.
                  A switch has to be read to be understood — which side is on? —
                  and it is a shape from a settings screen, not from a form.
                  The SAME box then appears in front of the event in Today, so
                  the thing you press and the thing you recognise later are one
                  picture. See RoundBtn.jsx. */}
              <CheckSquare on={form.allDay} size={22} />
              {/* The same class as LUOGO (OPZIONALE) and every other heading in
                  this form, rather than a copy of its numbers: this way they
                  cannot drift apart the next time one of them is touched.
                  Only the layout is overridden — the label sits beside its
                  switch here, not above a field. */}
              <span className="form-section-label" style={{ display: 'inline', marginBottom: 0 }}>
                {T.form.allDay}
              </span>
            </button>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>

            </div>
          </div>

          {/* ── Who else is busy at this time ────────────────────────────────
              These two notices live HERE, right under the time they are about,
              and no longer at the far bottom of the form: an overlap warning
              that appears below the notes, off-screen behind an open keyboard,
              is read (if at all) long after the moment it could have been
              useful. Neither one blocks saving. */}
          {conflictMembers.length > 0 && (
            <div style={{
              margin: '12px 0 0',
              padding: '12px 14px',
              borderRadius: 'var(--radius-sm)',
              background: 'rgba(230, 126, 34, 0.09)',
              border: '1.5px solid rgba(230, 126, 34, 0.45)',
            }}>
              <div style={{
                fontSize: '0.78rem', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.05em',
                color: '#E67E22', marginBottom: 8,
              }}>
                {T.form.conflictTitle || 'Schedule conflict'}
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: 8 }}>
                {T.form.conflictHint || 'Busy at this time:'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {conflictMembers.map((m, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: m.color, color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.72rem', fontWeight: 700, flexShrink: 0,
                    }}>
                      {m.name.charAt(0).toUpperCase()}
                    </div>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--color-text)' }}>
                      {m.name}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginInlineStart: 'auto' }}>
                      {m.time}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* One line, no box. It was a filled orange panel three lines tall
              standing between the time and the place — for something you are
              allowed to do anyway. Silvia: "visto che si può sempre salvare
              indica in rosso SOVRAPPOSIZIONE e indica a cosa si sovrappone".
              So: the word, in red, and what it runs into. "Puoi salvare
              comunque" is gone — nothing is stopping you, and saying so took
              a line to explain an absence.

              And when the other event has no name of its own, the time is
              given on its own rather than a pair of empty quotation marks,
              which is what the screen actually showed: «» (13:00). */}
          {personalClash && (
            <div style={{
              margin: '8px 0 0', display: 'flex', alignItems: 'baseline',
              gap: 7, flexWrap: 'wrap', fontSize: '0.8rem', lineHeight: 1.35,
            }}>
              <span style={{
                fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase',
                letterSpacing: '0.05em', color: '#C0392B', flexShrink: 0,
              }}>
                {T.form.overlapTitle || 'Overlap'}
              </span>
              {/* The same red as the word, and not bold: it is the same
                  message, said in detail. */}
              <span style={{ color: '#C0392B', fontWeight: 400, minWidth: 0, overflowWrap: 'break-word' }}>
                {personalClash.title
                  ? `«${personalClash.title}» (${personalClash.time})`
                  : `(${personalClash.time})`}
              </span>
            </div>
          )}
        </div>

        <Divider />

        {/* Everything from here to </form> is locked in edit mode until the
            user taps ✏️ to unlock. <fieldset disabled> propagates to every
            form control inside in one shot. minWidth:0 stops fieldset's
            default min-content sizing from breaking the flex layout.       */}
        <fieldset disabled={readOnly} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>

        {/* ── Location ─────────────────────────────────────────────────── */}
        <div style={{ padding: '14px 0' }}>
          <span className="form-section-label">{T.form.locationLabel}</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              className="form-input-flat"
              name="location"
              value={form.location}
              onChange={handleChange}
              placeholder={T.form.locationPlaceholder}
              aria-label={T.form.locationLabel}
              style={{ flex: 1 }}
            />
            {form.location.trim() && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(form.location)}`}
                target="_blank"
                rel="noopener noreferrer"
                title={T.form.openInMaps}
                style={{
                  flexShrink: 0,
                  width: 34, height: 34,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 'var(--radius-sm)',
                  border: '1.5px solid var(--color-border)',
                  background: 'var(--color-surface)',
                  fontSize: '1rem',
                  textDecoration: 'none',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-primary)'; e.currentTarget.style.background = 'rgba(122,158,126,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.background = 'var(--color-surface)'; }}
              >
                📍
              </a>
            )}
          </div>
        </div>

        <Divider />

        {/* ── Priority ─────────────────────────────────────────────────── */}
        <div style={{ padding: '14px 0' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <span className="form-section-label" style={{ marginBottom: 0, flexShrink: 0 }}>
              {T.form.priorityLabel}
            </span>
            {T.form.priorities.map((opt) => {
              const COLOR = {
                red:    'var(--color-stripe-red)',
                yellow: 'var(--color-stripe-yellow)',
                green:  'var(--color-stripe-green)',
              }[opt.value];
              const isSelected = form.priority === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  aria-label={`${T.form.priorityLabel} ${opt.label}`}
                  className="subcal-picker-btn"
                  style={{
                    // The same tone as the stripe on the card — they are the same
                    // three colours and they must look it. The unselected ones
                    // used to sit at 45% opacity, which washed them out until
                    // Silvia asked whether the pale look was deliberate: it was
                    // how 'not chosen' was being said, and it was saying it by
                    // lying about the colour.
                    //
                    // Chosen is now marked the way a chosen colour is marked
                    // everywhere else in MeMo — a dark ring around it, exactly
                    // like the swatch in the label palette.
                    // Chosen is FILLED, not chosen is OUTLINED — the same way
                    // the calendars row a few lines above says it, so the form
                    // has one way of showing a choice instead of two. The dark
                    // ring I tried first was a third way, and Silvia was right
                    // that it looked like a mistake on the green one.
                    background: isSelected ? COLOR : 'transparent',
                    color: isSelected ? '#fff' : COLOR,
                    fontWeight: 700,
                    border: '2px solid ' + COLOR,
                    opacity: 1,
                  }}
                  onClick={() => setForm((f) => ({ ...f, priority: opt.value }))}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Sub-calendar ─────────────────────────────────────────────── */}
        {subCalendars.length > 0 && (
          <>
            <Divider />
            <div style={{ padding: '14px 0' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-muted)', flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {T.form.subCalendarLabel}
                </span>
                {subCalendars.map(sc => (
                  <button
                    key={sc.id}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, subCalendarId: f.subCalendarId === sc.id ? '' : sc.id }))}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '4px 6px',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      fontFamily: 'inherit',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: sc.color,
                      textDecoration: form.subCalendarId === sc.id ? 'underline' : 'none',
                      textUnderlineOffset: '3px',
                    }}
                  >
                    {sc.name}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── Calendars (shared) — right under the label section ─────────── */}
        {userGroups.length > 0 && (
          <>
            <Divider />
            <div style={{ padding: '12px 0 6px' }}>
              <div style={{
                fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'var(--color-text-muted)', marginBottom: 10,
              }}>
                {T.form.visibilityLabel}
              </div>
              {/* Every calendar on screen at once — yours first, then each
                  shared one by name.
                  It used to be two buttons ("me" / "shared") and, only after
                  choosing "shared" AND only when you had more than one, a
                  native dropdown. Silvia added a second shared calendar and
                  reported it missing: it was not missing, it was two steps and
                  a grey Android dropdown away. A choice you cannot see is a
                  choice you do not have.
                  Same shape as the filter row on Today, so it is already
                  familiar: your name, then the calendars. */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { key: 'private', label: (user?.name || '').split(' ')[0] || T.form.visibilityPrivate, on: visibility === 'private' },
                  ...userGroups.map(g => ({
                    key: g.id,
                    label: g.name,
                    on: visibility === 'group' && selectedGroupId === g.id,
                  })),
                ].map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    aria-pressed={opt.on}
                    onClick={() => {
                      if (opt.key === 'private') { setVisibility('private'); return; }
                      setVisibility('group');
                      setSelectedGroupId(opt.key);
                    }}
                    style={{
                      flex: '1 1 auto', minWidth: 92, maxWidth: '100%',
                      padding: '7px 12px', fontSize: '0.66rem', fontWeight: 600,
                      borderRadius: 'var(--radius-sm)',
                      // Outlined in the app's colour whether chosen or not, so
                      // the row reads as a set of calendars you can pick from
                      // rather than one live button beside some grey ones. The
                      // chosen one is still unmistakable: it is filled.
                      border: '1.5px solid var(--color-primary)',
                      background: opt.on ? 'var(--color-primary)' : 'transparent',
                      color: opt.on ? '#fff' : 'var(--color-text)',
                      cursor: 'pointer', transition: 'all 0.15s',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <Divider />

        {/* ── Recurrence ───────────────────────────────────────────────── */}
        <div style={{ padding: '14px 0' }}>
          {isEdit && editEvent?.recurringEventId && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: '0.8rem', color: 'var(--color-text-muted)',
              marginBottom: 10, padding: '8px 10px',
              background: 'var(--color-surface)',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--color-border)',
            }}>
              <span style={{
                display: 'inline-flex', width: 18, height: 18,
                borderRadius: '50%', background: 'var(--color-primary)',
                color: '#fff', fontSize: '0.65rem',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>↻</span>
              {T.form.recurringNote}
            </div>
          )}
          <div>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 8 }}>
              {T.form.recurrenceLabel}
            </span>
            {/* 2×2 grid: Daily|Weekly on row 1, Monthly|Yearly on row 2 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              { freq: 'DAILY',   label: T.form.recurrenceDaily   },
              { freq: 'WEEKLY',  label: T.form.recurrenceWeekly  },
              { freq: 'MONTHLY', label: T.form.recurrenceMonthly },
              { freq: 'YEARLY',  label: T.form.recurrenceYearly  },
            ].map(({ freq, label }) => (
              <button
                key={freq}
                type="button"
                className={`subcal-picker-btn${form.recurrence.freq === freq ? ' selected' : ''}`}
                style={{ width: '100%', borderRadius: 'var(--radius-sm)' }}
                onClick={() => setForm(f => {
                  const newFreq = f.recurrence.freq === freq ? null : freq;
                  return {
                    ...f,
                    recurrence: { ...f.recurrence, freq: newFreq,
                      byDay: newFreq === 'WEEKLY'
                        ? [['SU','MO','TU','WE','TH','FR','SA'][new Date(f.datetime).getDay()]]
                        : f.recurrence.byDay,
                    },
                  };
                })}
              >
                {label}
              </button>
            ))}
            </div>{/* end 2×2 grid */}
          </div>{/* end recurrence section */}
          {/* #1 — interval: "Every N days / weeks / months / years" */}
          {form.recurrence.freq && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap', fontSize: '0.9rem' }}>
              <span>{T.form.recurrenceEvery}</span>
              <input
                type="number" min="1" max="99"
                value={form.recurrence.interval}
                onChange={e => setForm(f => ({ ...f, recurrence: { ...f.recurrence, interval: Math.max(1, parseInt(e.target.value) || 1) } }))}
                className="form-input"
                style={{ width: 60, padding: '4px 8px', display: 'inline-block' }}
              />
              <span>{
                form.recurrence.freq === 'DAILY'   ? T.form.recUnitDays   :
                form.recurrence.freq === 'WEEKLY'  ? T.form.recUnitWeeks  :
                form.recurrence.freq === 'MONTHLY' ? T.form.recUnitMonths :
                                                     T.form.recUnitYears
              }</span>
            </div>
          )}
          {form.recurrence.freq === 'WEEKLY' && (
            <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
              {['SU','MO','TU','WE','TH','FR','SA'].map((day, i) => {
                const selected = form.recurrence.byDay.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => setForm(f => ({
                      ...f,
                      recurrence: {
                        ...f.recurrence,
                        byDay: selected
                          ? f.recurrence.byDay.filter(d => d !== day)
                          : [...f.recurrence.byDay, day],
                      },
                    }))}
                    style={{
                      width: 38, height: 38, borderRadius: '50%', cursor: 'pointer',
                      border: `2px solid ${selected ? 'var(--color-primary)' : 'var(--color-border)'}`,
                      background: selected ? 'var(--color-primary)' : 'var(--color-card)',
                      color: selected ? '#fff' : 'var(--color-text)',
                      fontWeight: 700, fontSize: '0.75rem', transition: 'all 0.15s',
                    }}
                  >
                    {T.form.recurrenceDays[i]}
                  </button>
                );
              })}
            </div>
          )}
          {/* #2 — monthly: same day of month vs "every Nth <weekday>" */}
          {form.recurrence.freq === 'MONTHLY' && (() => {
            const d = new Date(form.datetime);
            const valid = !isNaN(d);
            const weekday = valid ? weekdayFmt.format(d) : '';
            const nth = valid ? nthWeekdayOfMonth(d) : 1;
            const ordLabel = nth === -1 ? T.form.recurrenceLast : `${nth}°`;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '0.9rem' }}>
                  <input type="radio" name="monthlyMode"
                    checked={form.recurrence.monthlyMode !== 'weekday'}
                    onChange={() => setForm(f => ({ ...f, recurrence: { ...f.recurrence, monthlyMode: 'day' } }))} />
                  {T.form.recurrenceMonthlyDay}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '0.9rem', flexWrap: 'wrap' }}>
                  <input type="radio" name="monthlyMode"
                    checked={form.recurrence.monthlyMode === 'weekday'}
                    onChange={() => setForm(f => ({ ...f, recurrence: { ...f.recurrence, monthlyMode: 'weekday' } }))} />
                  {T.form.recurrenceEvery} {ordLabel} {weekday}
                </label>
              </div>
            );
          })()}
          {form.recurrence.freq && (
            <div style={{ marginTop: 14, borderTop: '1px solid var(--color-border)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <span className="form-section-label">
                {T.form.recurrenceEnds}
              </span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '0.9rem' }}>
                <input type="radio" name="recEndType" value="never"
                  checked={form.recurrence.endType === 'never'}
                  onChange={() => setForm(f => ({ ...f, recurrence: { ...f.recurrence, endType: 'never' } }))} />
                {T.form.recurrenceNever}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '0.9rem', flexWrap: 'wrap' }}>
                <input type="radio" name="recEndType" value="count"
                  checked={form.recurrence.endType === 'count'}
                  onChange={() => setForm(f => ({ ...f, recurrence: { ...f.recurrence, endType: 'count' } }))} />
                <input
                  type="number" min="1" max="365"
                  value={form.recurrence.count}
                  onChange={e => setForm(f => ({ ...f, recurrence: { ...f.recurrence, count: Math.max(1, parseInt(e.target.value) || 1), endType: 'count' } }))}
                  className="form-input"
                  style={{ width: 64, padding: '4px 8px', display: 'inline-block' }}
                />
                {T.form.recurrenceTimes}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '0.9rem', flexWrap: 'wrap' }}>
                <input type="radio" name="recEndType" value="until"
                  checked={form.recurrence.endType === 'until'}
                  onChange={() => setForm(f => ({ ...f, recurrence: { ...f.recurrence, endType: 'until' } }))} />
                {T.form.recurrenceOnDate}
                <input
                  type="date"
                  value={form.recurrence.until}
                  onChange={e => setForm(f => ({ ...f, recurrence: { ...f.recurrence, until: e.target.value, endType: 'until' } }))}
                  className="form-input"
                  style={{ flex: 1, minWidth: 130, padding: '4px 8px', display: 'inline-block', background: 'var(--color-card)', border: '2px solid var(--color-primary)' }}
                />
              </label>
            </div>
          )}
        </div>

        <Divider />

        {/* ── Reminder ─────────────────────────────────────────────────── */}
        <div style={{ padding: '14px 0' }}>
          {/* Header: label only — reminders are dictated with the whole event
              (main mic) or set by hand below. */}
          <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className="form-section-label" style={{ display: 'inline', marginBottom: 0 }}>
              {T.form.reminderLabel}
            </span>
            {alertsState === 'loading' && (
              <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span style={{ animation: 'spin 0.8s linear infinite', display: 'inline-block' }}>⏳</span>
                {REMINDERS_LOADING[baseLang] || REMINDERS_LOADING.en}
              </span>
            )}
          </div>

          {/* The reminders could not be read — say it, and say that saving is
              still fine. Without this the section would look like "no reminders
              set", which is a lie the user would act on. */}
          {alertsState === 'failed' && (
            <div style={{
              marginBottom: 10, padding: '10px 12px', borderRadius: 'var(--radius-sm)',
              background: 'rgba(230, 126, 34, 0.09)', border: '1.5px solid rgba(230, 126, 34, 0.45)',
              fontSize: '0.82rem', color: 'var(--color-text)', lineHeight: 1.4,
            }}>
              {REMINDERS_LOAD_FAILED[baseLang] || REMINDERS_LOAD_FAILED.en}
            </div>
          )}

          {/* Selected times as dismissible chips */}
          {alertSet.size > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {[...alertSet].sort((a, b) => a - b).map(mins => {
                const opt = T.form.reminderOptions.find(o => o.value === mins);
                return (
                  <button
                    key={mins}
                    type="button"
                    onClick={() => toggleAlert(mins)}
                    className="subcal-picker-btn selected"
                    style={{ display: 'flex', alignItems: 'center', gap: 5 }}
                  >
                    {opt ? opt.label : `${mins} min`}
                    {/* currentColor: green on a plain background, white on a
                        filled chip — the same drawing either way. */}
                    <span style={{ display: 'flex', opacity: 0.85 }}><TrashIcon size={13} /></span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Add-reminder select + bell (alarm) toggle on the same row.
              Bell filled/green = also rings; bell outline = silent notification. */}
          {(alertSet.size < T.form.reminderOptions.length || alertSet.size > 0) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {alertSet.size < T.form.reminderOptions.length && (
                <select
                  value=""
                  onChange={e => {
                    const v = Number(e.target.value);
                    if (e.target.value !== '' && !isNaN(v)) toggleAlert(v);
                    e.target.value = '';
                  }}
                  style={{
                    flex: 1, padding: '9px 12px', borderRadius: 'var(--radius-sm)',
                    border: '1.5px solid var(--color-border)', background: 'var(--color-surface)',
                    color: alertSet.size === 0 ? 'var(--color-text-muted)' : 'var(--color-text)',
                    fontSize: '0.88rem', cursor: 'pointer', appearance: 'auto',
                  }}
                >
                  <option value="">＋ {T.form.reminderLabel}…</option>
                  {T.form.reminderOptions
                    .filter(o => !alertSet.has(o.value))
                    .map(o => <option key={o.value} value={o.value}>{o.label}</option>)
                  }
                </select>
              )}
              {alertSet.size > 0 && (
                <button
                  type="button"
                  onClick={() => setAlertType(t => (t === 'alarm' ? 'message' : 'alarm'))}
                  title={(ALERT_TYPE_L[baseLang] || ALERT_TYPE_L.it).alarm}
                  aria-label={(ALERT_TYPE_L[baseLang] || ALERT_TYPE_L.it).alarm}
                  style={{
                    flexShrink: 0, width: 44, height: 44, borderRadius: 'var(--radius-sm)',
                    border: `1.5px solid ${alertType === 'alarm' ? 'var(--color-primary)' : 'var(--color-border)'}`,
                    background: alertType === 'alarm' ? 'var(--color-primary)' : 'var(--color-surface)',
                    color: alertType === 'alarm' ? '#fff' : 'var(--color-text-muted)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  <BellIcon size={20} />
                </button>
              )}
            </div>
          )}
        </div>

        <Divider />

        {/* ── Notes ────────────────────────────────────────────────────── */}
        <div style={{ padding: '14px 0 8px' }}>
          <textarea
            className="form-textarea notes-textarea"
            name="notes"
            value={form.notes}
            onChange={handleChange}
            placeholder={T.form.notesPlaceholder}
            aria-label={T.form.notesLabel}
          />
        </div>

        {/* ── Attachments (photos / PDF) — the round 📎 button (new mode) or the
            add button below (edit mode) fills this list ─────────────────── */}
        <div style={{ padding: '4px 0 8px' }}>
          <div className="form-section-label">
            {T.form.attLabel}
          </div>
          {(existingAtt.length > 0 || pendingFiles.length > 0) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
              {existingAtt.map(a => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: '0.85rem' }}>
                  <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{a.mime === 'application/pdf' ? '📄' : '🖼️'}</span>
                  <a href={a.url} target="_blank" rel="noreferrer" style={{ flex: 1, minWidth: 0, color: 'var(--color-primary-dark)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.filename}</a>
                  {a.mine && <button type="button" onClick={() => removeExisting(a.id)} aria-label={T.form.deleteButton} style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: 'var(--color-primary)', display: 'flex', flexShrink: 0 }}><TrashIcon size={15} /></button>}
                </div>
              ))}
              {pendingFiles.map((f, i) => (
                <div key={'p' + i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: '0.85rem' }}>
                  <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{f.mime === 'application/pdf' ? '📄' : '🖼️'}</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                  <button type="button" onClick={() => removePending(i)} aria-label={T.form.deleteButton} style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: 'var(--color-primary)', display: 'flex', flexShrink: 0 }}><TrashIcon size={15} /></button>
                </div>
              ))}
            </div>
          )}
          <input ref={attInputRef} type="file" accept="image/*,application/pdf" multiple style={{ display: 'none' }} onChange={handlePickFiles} />
          {isEdit && existingAtt.length + pendingFiles.length < ATT_MAX && (
            <button type="button" onClick={() => attInputRef.current?.click()} className="btn btn-outline btn-sm">
              {T.form.attAdd}
            </button>
          )}
          {attBusy && (
            <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ animation: 'spin 0.8s linear infinite', display: 'inline-block' }}>⏳</span>
              {PREPARING_LABELS[baseLang] || PREPARING_LABELS.en}
            </div>
          )}
          <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: 6 }}>{T.form.attHint}</div>
          {attError && <div style={{ fontSize: '0.75rem', color: '#e63946', marginTop: 4 }}>{attError}</div>}
        </div>

        </fieldset>

        {/* ── Hidden file inputs ──────────────────────────────────────────── */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleImageScan}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleCameraCapture}
        />

        {/* ── Camera menu popover — opens upward above the action bar ─────── */}
        {showImageMenu && !isEdit && (
          <>
            {/* Invisible backdrop — tap to close */}
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 199 }}
              onClick={() => setShowImageMenu(false)}
            />
            <div style={{
              position: 'fixed',
              bottom: 'calc(64px + env(safe-area-inset-bottom, 0px) + 20px + 176px)',
              right: 92,
              zIndex: 200,
              background: 'var(--color-card)',
              border: '1.5px solid var(--color-border)',
              borderRadius: 14,
              overflow: 'hidden',
              boxShadow: '0 -4px 24px rgba(0,0,0,0.18)',
              minWidth: 170,
            }}>
              <button
                type="button"
                aria-label={((CAM_MENU_LABELS[baseLang] || CAM_MENU_LABELS.en).camera).replace(/^\S+\s*/, '')}
                onClick={() => { setShowImageMenu(false); cameraInputRef.current?.click(); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '13px 16px', background: 'none', border: 'none', fontSize: '0.92rem', fontWeight: 600, cursor: 'pointer', color: 'var(--color-text)' }}
              >
                {(CAM_MENU_LABELS[baseLang] || CAM_MENU_LABELS.en).camera}
              </button>
              <div style={{ height: 1, background: 'var(--color-border)' }} />
              <button
                type="button"
                aria-label={((CAM_MENU_LABELS[baseLang] || CAM_MENU_LABELS.en).gallery).replace(/^\S+\s*/, '')}
                onClick={() => { setShowImageMenu(false); imageInputRef.current?.click(); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '13px 16px', background: 'none', border: 'none', fontSize: '0.92rem', fontWeight: 600, cursor: 'pointer', color: 'var(--color-text)' }}
              >
                {(CAM_MENU_LABELS[baseLang] || CAM_MENU_LABELS.en).gallery}
              </button>
            </div>
          </>
        )}

        </div>

        {/* ── Floating action buttons — new mode only. A vertical stack of round
            buttons on the right, top→bottom: Attach, Camera (photo/gallery),
            Mic (dictate), OK (save). Spaced apart so a thumb tapping one doesn't
            catch its neighbour. Replaces the old horizontal bar that blocked
            scrolling. Edit mode uses the inline OK / delete on the all-day row. */}
        {!isEdit && (() => {
          const fabBase = {
            width: 64, height: 64, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: 'none', color: '#fff',
            boxShadow: '0 3px 12px rgba(78,122,82,0.45)',
            flexShrink: 0,
          };
          return (
            <div
              // Out of the way while you are reading, back when you reach for
              // them. Four 64px circles down the right edge sit on top of the
              // form — on a small screen they cover the reminder row, and the
              // only way to read underneath was to scroll the page up and down.
              //
              // They fade instead of moving: a control that jumps around is
              // worse than one you can see through. Full strength returns the
              // moment a finger touches the column, and after a second of
              // stillness — so reaching for them never means aiming at a ghost.
              //
              // Not applied to OK. A save button that goes faint while you type
              // is the one thing here that must always look pressable.
              style={{
                position: 'fixed',
                insetInlineEnd: 16,
                bottom: 'calc(64px + env(safe-area-inset-bottom, 0px) + 20px)',
                zIndex: 100,
                display: 'flex', flexDirection: 'column', gap: 18, alignItems: 'center',
              }}
              onPointerDown={() => setFabDimmed(false)}
            >
              {/* The three that can fade. OK is deliberately outside this
                  group: a save button going faint while you type is the one
                  thing here that must always look pressable. */}
              <div style={{
                display: 'flex', flexDirection: 'column', gap: 18, alignItems: 'center',
                opacity: fabDimmed ? 0.32 : 1,
                transition: 'opacity 0.22s ease-out',
              }}>
              {/* Top — Attach photo / PDF */}
              <button
                type="button"
                onClick={() => attInputRef.current?.click()}
                title={T.form.attLabel}
                aria-label={T.form.attLabel}
                style={{ ...fabBase, background: 'var(--color-primary)', fontSize: '1.9rem', cursor: 'pointer' }}
              >
                <ClipIcon />
              </button>

              {/* Camera → photo / gallery menu */}
              <button
                type="button"
                onClick={() => setShowImageMenu(v => !v)}
                title={T.form.photoLabel}
                aria-label={T.form.photoLabel}
                aria-busy={preparingPhoto || imageParsing}
                disabled={preparingPhoto || imageParsing}
                style={{ ...fabBase, background: showImageMenu ? 'var(--color-primary-dark)' : 'var(--color-primary)', fontSize: '2rem', cursor: 'pointer' }}
              >
                {(preparingPhoto || imageParsing)
                  ? <span style={{ fontSize: '1.6rem', letterSpacing: 1, lineHeight: 1 }}>…</span>
                  : <CameraIcon />}
              </button>

              {/* Mic → dictate the event (re-tap or 3 s of silence stops) */}
              <button
                type="button"
                onClick={handleMainMicToggle}
                disabled={mainTrans}
                title={T.form.dictateLabel}
                aria-label={T.form.dictateLabel}
                style={{ ...fabBase, background: (mainRec || mainTrans) ? '#c4614a' : 'var(--color-primary)', fontSize: '2rem', cursor: mainTrans ? 'not-allowed' : 'pointer' }}
              >
                {mainTrans ? '…' : mainRec ? <StopIcon /> : <MicIcon />}
              </button>

              </div>

              {/* Bottom — Save (labelled OK) */}
              <button
                type="submit"
                disabled={saving || parsing}
                title={T.form.saveButton}
                aria-label={T.form.saveButton}
                style={{ ...fabBase, background: 'var(--color-primary)', fontSize: '1.12rem', fontWeight: 800, letterSpacing: '0.03em', opacity: (saving || parsing) ? 0.55 : 1, cursor: (saving || parsing) ? 'not-allowed' : 'pointer' }}
              >
                {saving ? '⏳' : <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" stroke="currentColor" fill="none" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4.5 4.5L19 7"/></svg>}
              </button>
            </div>
          );
        })()}

        {/* ── What the save is doing right now ────────────────────────────
            Saving is a chain: the event, then the reminders, then the files,
            one after another. With a 10 MB photo on a slow connection that is
            tens of seconds behind a button showing nothing but an hourglass —
            long enough to look like a crash and be tapped again. */}
        {saveStage && !(saveStage.kind === 'files' && !saveStage.total) && (
          <div style={{
            position: 'fixed',
            insetInlineStart: 16,
            insetInlineEnd: 96,
            bottom: 'calc(64px + env(safe-area-inset-bottom, 0px) + 20px)',
            zIndex: 99,
            padding: '10px 14px',
            borderRadius: 999,
            background: 'var(--color-card)',
            border: '1.5px solid var(--color-border)',
            boxShadow: '0 3px 14px rgba(0,0,0,0.16)',
            fontSize: '0.82rem', fontWeight: 600, color: 'var(--color-text)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ animation: 'spin 0.8s linear infinite', display: 'inline-block' }}>⏳</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {saveStage.kind === 'event'  ? SS.event
                : saveStage.kind === 'alerts' ? SS.alerts
                : `${SS.files} ${saveStage.done}/${saveStage.total}…`}
            </span>
          </div>
        )}

        {/* ── Edit mode: round OK (save) + delete, matching the "+" stack ── */}
        {isEdit && (
          <div style={{
            position: 'fixed',
            insetInlineEnd: 16,
            bottom: 'calc(64px + env(safe-area-inset-bottom, 0px) + 20px)',
            zIndex: 100,
            display: 'flex', flexDirection: 'column', gap: 18, alignItems: 'center',
          }}>
            {/* Save (OK) — round green, like the "+" form.
                Also held back while the reminders are still being read: saving
                during that window would have used an empty reminder list. It is
                a fraction of a second on a normal connection. */}
            <button
              type="submit"
              disabled={saving || parsing || alertsState === 'loading'}
              title={alertsState === 'loading'
                ? (REMINDERS_LOADING[baseLang] || REMINDERS_LOADING.en)
                : T.form.saveButton}
              aria-label={T.form.saveButton}
              style={{
                width: 64, height: 64, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: 'none', color: '#fff', background: 'var(--color-primary)',
                // The same 1.12rem as the OK on a new event and the one in
                // Notes. This is the third copy of the same button and the
                // only one that had been missed.
                fontSize: '1.12rem', fontWeight: 800, letterSpacing: '0.03em',
                boxShadow: '0 3px 12px rgba(78,122,82,0.45)',
                cursor: (saving || parsing || alertsState === 'loading') ? 'not-allowed' : 'pointer',
                opacity: (saving || parsing || alertsState === 'loading') ? 0.55 : 1,
              }}
            >
              {saving ? '⏳' : alertsState === 'loading' ? '…' : <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" stroke="currentColor" fill="none" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4.5 4.5L19 7"/></svg>}
            </button>
            {/* Delete — round, green (not red) as requested */}
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting || saving}
              title={T.form.deleteButton}
              aria-label={T.form.deleteButton}
              style={{
                width: 64, height: 64, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '2px solid var(--color-primary)', color: 'var(--color-primary-dark)',
                background: 'var(--color-card)', fontSize: '1.6rem',
                boxShadow: '0 3px 12px rgba(0,0,0,0.18)',
                cursor: (deleting || saving) ? 'not-allowed' : 'pointer',
                opacity: (deleting || saving) ? 0.5 : 1,
              }}
            >
              {deleting ? '⏳' : <TrashIcon size={26} />}
            </button>
          </div>
        )}

      </form>
    </>
  );
}
